import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, X, ExternalLink, Loader2, LogOut, User as UserIcon, SlidersHorizontal, Sparkles, Check, ArrowLeft } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOrgTransitions, getEmployeeTransitions, getAlumni, getCurrentEmployees, searchOrganizations, getProfile, getDashboardData, getLinkedinOrgIdsByCompanyNames, addNotifyRequest, addReferral, getSimilarCompanies } from '../api/insights';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import {
  trackCoreFeatureUsed,
  incrementActivationCounter,
  trackCompanySearch,
  trackCompanyClicked,
  trackFindingJobs,
} from '../analytics/mixpanel';
import { toast } from 'sonner';

const CONNECTION_FILTERS_STORAGE_KEY = 'insightsConnectionFiltersState';
const DASHBOARD_STATE_STORAGE_KEY = 'insightsDashboardState';
const DASHBOARD_TOUR_STORAGE_KEY = 'insights_dashboard_tour_seen_v1';
const SAVED_ROLES_STORAGE_KEY = 'insights_saved_roles';

const readSavedRoles = (userId) => {
  if (typeof window === 'undefined') return [];
  try {
    const key = `${SAVED_ROLES_STORAGE_KEY}_${userId || 'anon'}`;
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSavedRoles = (userId, roles) => {
  if (typeof window === 'undefined') return;
  try {
    const key = `${SAVED_ROLES_STORAGE_KEY}_${userId || 'anon'}`;
    const uniqueRoles = [...new Set(roles)].filter(Boolean).slice(0, 15);
    localStorage.setItem(key, JSON.stringify(uniqueRoles));
  } catch {
    // ignore
  }
};
const ALL_PROFILE_COMPANIES_TOKEN = '__all_profile_companies__';
const ALL_PROFILE_COLLEGES_TOKEN = '__all_profile_colleges__';
const ANY_ROLE_TOKEN = '__any_role__';
const ANY_DEPARTMENT_TOKEN = '__any_department__';
const DASHBOARD_TOUR_STEPS = [
  {
    key: 'context',
    title: 'Find Career Paths',
    description: 'Find where people from your background are getting hired. Enter a company, role, and time period to explore career moves.'
  },
  {
    key: 'filters-group',
    title: 'Search and filter',
    description: 'Search name: Search any company to see if it appears in the transition list. Search role: Filter by role to see which companies hired for this specific position. Year: Select a year to view transitions that happened then. Tip — pick a year close to when you left this company. Transition: See where people go after their 1st, 2nd, or 3rd job after leaving this company.'
  },
  {
    key: 'people-other',
    title: 'People (other transitions)',
    description: 'Count of employees who worked elsewhere after {{company b}}, but eventually landed at {{company a}}.'
  },
  {
    key: 'recent',
    title: 'Recent',
    description: 'Last hired from {{company b}} in {{year}}.'
  },
  {
    key: 'years-list',
    title: 'Years list',
    description: 'Years when {{company a}} hired from {{company b}}.'
  },
  {
    key: 'connections-filter',
    title: 'Filters',
    description: 'Filter companies where you already have connections — from your past workplaces or college.'
  },
  {
    key: 'company-card',
    description: 'Click on this to view people involved in transition or related to your background.'
  },
];

function loadSavedConnectionFiltersState(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return {
        selectedPastCompanies: [],
        selectedPastRoles: [],
        selectedTenureOptions: [],
        selectedColleges: [],
        selectedDepartments: [],
        selectedBatchOptions: [],
        appliedConnectionFilters: null,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      selectedPastCompanies: Array.isArray(parsed.selectedPastCompanies) ? parsed.selectedPastCompanies : [],
      selectedPastRoles: Array.isArray(parsed.selectedPastRoles) ? parsed.selectedPastRoles : [],
      selectedTenureOptions: Array.isArray(parsed.selectedTenureOptions) ? parsed.selectedTenureOptions : [],
      selectedColleges: Array.isArray(parsed.selectedColleges) ? parsed.selectedColleges : [],
      selectedDepartments: Array.isArray(parsed.selectedDepartments) ? parsed.selectedDepartments : [],
      selectedBatchOptions: Array.isArray(parsed.selectedBatchOptions) ? parsed.selectedBatchOptions : [],
      appliedConnectionFilters: parsed.appliedConnectionFilters && typeof parsed.appliedConnectionFilters === 'object'
        ? parsed.appliedConnectionFilters
        : null,
    };
  } catch {
    return {
      selectedPastCompanies: [],
      selectedPastRoles: [],
      selectedTenureOptions: [],
      selectedColleges: [],
      selectedDepartments: [],
      selectedBatchOptions: [],
      appliedConnectionFilters: null,
    };
  }
}

/**
 * Transform API /org-transitions response into two sets of company cards:
 * - 1st Transitions view
 * - 2nd Transitions view
 *
 * API shape (per hop key "1", "2", ...):
 * {
 *   "1": [
 *     {
 *       organization_id,
 *       organization,
 *       count,       // people who reached this org at hop 1
 *       total_count, // people who reached this org across *all* hops
 *       years: [...]
 *     },
 *     ...
 *   ],
 *   "2": [...]
 * }
 */
function mapTransitions(apiResult, relatedByDest) {
  if (!apiResult || typeof apiResult !== 'object') {
    return { firstHopCompanies: [], secondHopCompanies: [] };
  }

  const hops = Object.keys(apiResult).filter(k => k !== 'related_by_dest');
  if (hops.length === 0) {
    return { firstHopCompanies: [], secondHopCompanies: [] };
  }

  // related_by_dest: { "org_id_str": { count, related: [...] } }
  const relMap = (relatedByDest && typeof relatedByDest === 'object') ? relatedByDest : {};

  // Build a helper map of all hops per destination org
  const perOrg = new Map();

  Object.entries(apiResult).forEach(([hopStr, list]) => {
    const hopNum = Number(hopStr);
    if (!Array.isArray(list) || Number.isNaN(hopNum)) return;
    list.forEach((item) => {
      const orgId = item.organization_id;
      const orgName = item.organization;
      if (orgId == null || !orgName) return;

      if (!perOrg.has(orgId)) {
        perOrg.set(orgId, {
          organizationId: orgId,
          name: orgName,
          years: new Set(),
          hops: {}, // { [hopNumber]: count }
          totalCount: 0,
          transitionMatchCount: 0,
        });
      }
      const entry = perOrg.get(orgId);
      entry.hops[hopNum] = (entry.hops[hopNum] || 0) + (item.count || 0);
      entry.totalCount =
        item.total_count != null ? item.total_count : entry.totalCount + (item.count || 0);
      entry.transitionMatchCount = Math.max(
        Number(entry.transitionMatchCount || 0),
        Number(item.transition_match_count || 0)
      );
      (item.years || []).forEach((y) => entry.years.add(y));
    });
  });

  // Helper to build card objects sorted by a specific hop
  function buildCardsForHop(hopNum) {
    const cards = [];
    perOrg.forEach((entry) => {
      const hopCount = entry.hops[hopNum] || 0;
      if (!hopCount) return;
      const yearsArr = Array.from(entry.years);
      const recentYear = yearsArr.length ? Math.max(...yearsArr) : null;
      const otherHopsCount = entry.totalCount - hopCount;
      const relEntry = relMap[String(entry.organizationId)] || null;
      const relatedCount = relEntry ? ((relEntry.match_count ?? relEntry.count) || 0) : 0;
      const transitionMatchCount = Number(entry.transitionMatchCount || 0);
      const filterMatchCount = relatedCount + transitionMatchCount;

      cards.push({
        organizationId: entry.organizationId,
        name: entry.name,
        // Primary people metric for this section (1st / 2nd transitions)
        people: hopCount,
        // People who reached via the first hop
        firstHopCount: entry.hops[1] || 0,
        // People who reached via the second hop
        secondHopCount: entry.hops[2] || 0,
        // All people across all hops
        totalPeople: entry.totalCount,
        // People who reached via hops other than this hop
        otherHopsCount,
        recent: recentYear,
        // All hops breakdown (used for filtering/tooltip/etc.)
        transitions: Object.entries(entry.hops)
          .map(([h, c]) => ({ moves: Number(h), count: c }))
          .sort((a, b) => a.moves - b.moves),
        years: yearsArr.sort((a, b) => b - a),
        // Related background: current employees sharing user's org/school background
        relatedCount,
        transitionMatchCount,
        filterMatchCount,
        relatedBackground: relEntry || null,
      });
    });

    // Sort: prioritize people (transition count) as primary sort, 
    // and relatedCount as a secondary tie-breaker.
    cards.sort((a, b) =>
      b.people - a.people ||
      b.filterMatchCount - a.filterMatchCount ||
      b.relatedCount - a.relatedCount ||
      a.name.localeCompare(b.name)
    );

    // Add rank
    return cards.map((c, idx) => ({ ...c, rank: idx + 1 }));
  }

  const firstHopCompanies = buildCardsForHop(1);
  const secondHopCompanies = buildCardsForHop(2);
  const thirdHopCompanies = buildCardsForHop(3);

  return { firstHopCompanies, secondHopCompanies, thirdHopCompanies };
}

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, openLogin, loading: authLoading } = useAuth();
  const userStorageSuffix = String(user?.id || user?.email || 'anonymous');
  const dashboardStateStorageKey = `${DASHBOARD_STATE_STORAGE_KEY}:${userStorageSuffix}`;
  const connectionFiltersStorageKey = `${CONNECTION_FILTERS_STORAGE_KEY}:${userStorageSuffix}`;
  const stateFromRoute = location.state || {};
  const stateFromStorage = (() => {
    try {
      const raw = sessionStorage.getItem(dashboardStateStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();
  const searchParams = { ...stateFromStorage, ...stateFromRoute };
  const savedConnectionFilters = loadSavedConnectionFiltersState(connectionFiltersStorageKey);

  const [topCompanyName, setTopCompanyName] = useState(searchParams.companyName || '');
  const [topStartYear, setTopStartYear] = useState(searchParams.startYear || '');
  const [topEndYear, setTopEndYear] = useState(searchParams.endYear || '');
  const [updatingOrgContext, setUpdatingOrgContext] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedTransition, setSelectedTransition] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [minTransitions, setMinTransitions] = useState('');
  const [maxTransitions, setMaxTransitions] = useState('');
  const [dataBusinessRoles, setDataBusinessRoles] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [orgSuggestions, setOrgSuggestions] = useState([]);
  const [showOrgSuggestions, setShowOrgSuggestions] = useState(false);
  const [orgSuggestionsLoading, setOrgSuggestionsLoading] = useState(false);
  const [hasTouchedCompanyInput, setHasTouchedCompanyInput] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [activeTab, setActiveTab] = useState('company-pathways');

  const [companiesFirst, setCompaniesFirst] = useState([]);
  const [companiesSecond, setCompaniesSecond] = useState([]);
  const [companiesThird, setCompaniesThird] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [alumniPage, setAlumniPage] = useState(1);
  const [employeesPage, setEmployeesPage] = useState(1);
  const [alumniTotal, setAlumniTotal] = useState(0);
  const [employeesTotal, setEmployeesTotal] = useState(0);
  const [alumniLoadingMore, setAlumniLoadingMore] = useState(false);
  const [employeesLoadingMore, setEmployeesLoadingMore] = useState(false);
  const [relatedByDest, setRelatedByDest] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingAlumni, setLoadingAlumni] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesFetched, setEmployeesFetched] = useState(false);
  const [error, setError] = useState(null);
  const [totalAlumni, setTotalAlumni] = useState(0);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [similarCompaniesLoading, setSimilarCompaniesLoading] = useState(false);

  // role-based highlighting state
  const [contextRole, setContextRole] = useState(stateFromStorage.role || '');
  const [contextApplying, setContextApplying] = useState(false);
  const [highlightedOrgIds, setHighlightedOrgIds] = useState(new Set());
  const [selectedCards, setSelectedCards] = useState(new Map());
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [notifiedCompanies, setNotifiedCompanies] = useState(new Set()); // track companies user asked to be notified about
  const [notifyingCompanies, setNotifyingCompanies] = useState(new Set()); // track companies currently being notified (loading state)
  const alumniCacheRef = useRef(new Map());
  const employeesCacheRef = useRef(new Map());
  const { logout } = useAuth();
  // Removed long mockCompanies list
  const years = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i);
  const filterYears = useMemo(() => {
    const start = parseInt(searchParams.startYear, 10);
    const end = parseInt(searchParams.endYear, 10);
    if (isNaN(start) || isNaN(end) || start > end) {
      return years;
    }
    const result = [];
    for (let y = end; y >= start; y--) {
      result.push(y);
    }
    return result;
  }, [searchParams.startYear, searchParams.endYear, years]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourPanelPos, setTourPanelPos] = useState({ top: 24, left: 24, width: 420 });
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleInput, setRoleInput] = useState('');
  const [searchRoles, setSearchRoles] = useState(() => readSavedRoles(user?.id));

  const updateSearchRoles = useCallback((next) => {
    const unique = [...new Set(next)].filter(Boolean).slice(0, 10);
    setSearchRoles(unique);
    writeSavedRoles(user?.id, unique);
  }, [user?.id]);
  // Multi-select connection filters (arrays). Empty = All/Any for that category.
  const [selectedPastCompanies, setSelectedPastCompanies] = useState(savedConnectionFilters.selectedPastCompanies);
  const [selectedPastRoles, setSelectedPastRoles] = useState(savedConnectionFilters.selectedPastRoles);
  const [selectedTenureOptions, setSelectedTenureOptions] = useState(savedConnectionFilters.selectedTenureOptions);
  const [selectedColleges, setSelectedColleges] = useState(savedConnectionFilters.selectedColleges);
  const [selectedDepartments, setSelectedDepartments] = useState(savedConnectionFilters.selectedDepartments);
  const [selectedBatchOptions, setSelectedBatchOptions] = useState(savedConnectionFilters.selectedBatchOptions);
  // Applied filters sent to API (set when user clicks Done in modal)
  const [appliedConnectionFilters, setAppliedConnectionFilters] = useState(savedConnectionFilters.appliedConnectionFilters);
  const [showNoMatchHint, setShowNoMatchHint] = useState(false);
  const [showContextMismatchHint, setShowContextMismatchHint] = useState(false);
  const [connectionFilterApplyTick, setConnectionFilterApplyTick] = useState(0);
  const lastFetchKeyRef = useRef(null);
  const alumniHasMore = (alumni?.length || 0) < (alumniTotal || 0);
  const employeesHasMore = (employees?.length || 0) < (employeesTotal || 0);
  // Reset inner year filter if out of range
  useEffect(() => {
    if (selectedYear === 'all') return;
    const yVal = parseInt(selectedYear, 10);
    if (!filterYears.includes(yVal)) {
      setSelectedYear('all');
    }
  }, [filterYears, selectedYear]);

  useEffect(() => {
    setSearchRoles(readSavedRoles(user?.id));
  }, [user?.id]);

  const transitionsRequestSeqRef = useRef(0);
  const firstTransitionsRef = useRef(null);
  const secondTransitionsRef = useRef(null);
  const thirdTransitionsRef = useRef(null);
  const hasAutoShownTourRef = useRef(false);
  const tourSeenStorageKey = useMemo(
    () => `${DASHBOARD_TOUR_STORAGE_KEY}:${String(user?.id || user?.email || 'anonymous')}`,
    [user?.id, user?.email]
  );

  const isProfileEmpty = !profile || !(profile.work_experiences && profile.work_experiences.length > 0);
  const profileCompanies = useMemo(() => {
    const work = profile?.work_experiences;
    if (!work || !Array.isArray(work) || work.length === 0) return [];
    const seen = new Set();
    return work
      .map((e) => e && e.company)
      .filter(Boolean)
      .filter((c) => {
        if (seen.has(c)) return false;
        seen.add(c);
        return true;
      });
  }, [profile]);
  const profileRoles = useMemo(() => {
    const work = profile?.work_experiences;
    if (!work || !Array.isArray(work) || work.length === 0) return [];
    const seen = new Set();
    return work
      .map((e) => e && e.role)
      .filter(Boolean)
      .filter((r) => {
        if (seen.has(r)) return false;
        seen.add(r);
        return true;
      });
  }, [profile]);

  const profileColleges = useMemo(() => {
    const educ = profile?.educations;
    if (!educ || !Array.isArray(educ) || educ.length === 0) return [];
    const seen = new Set();
    return educ
      .map((e) => e && e.college)
      .filter(Boolean)
      .filter((c) => {
        if (seen.has(c)) return false;
        seen.add(c);
        return true;
      });
  }, [profile]);

  const profileDepartments = useMemo(() => {
    const educ = profile?.educations;
    if (!educ || !Array.isArray(educ) || educ.length === 0) return [];
    const seen = new Set();
    return educ
      .map((e) => e && e.degree)
      .filter(Boolean)
      .filter((d) => {
        if (seen.has(d)) return false;
        seen.add(d);
        return true;
      });
  }, [profile]);

  const clearAllFilters = () => {
    setSearchName('');
    setSelectedYear('all');
    setSelectedTransition('all');
    setSelectedStatus('all');
    setMinTransitions('');
    setMaxTransitions('');
    setDataBusinessRoles(false);
    setSelectedPastCompanies([]);
    setSelectedPastRoles([]);
    setSelectedTenureOptions([]);
    setSelectedColleges([]);
    setSelectedDepartments([]);
    setSelectedBatchOptions([]);
    setAppliedConnectionFilters(null);
    setShowNoMatchHint(false);
    setShowContextMismatchHint(false);
    try {
      sessionStorage.removeItem(connectionFiltersStorageKey);
    } catch (_) { }
  };

  const getTopCompanyNamesForPerson = (person) => {
    const seen = new Set();
    const names = [];
    const path = Array.isArray(person?.path) ? person.path : [];
    for (const company of path) {
      const value = String(company || '').trim();
      const key = value.toLowerCase();
      if (!value || seen.has(key)) continue;
      seen.add(key);
      names.push(value);
      if (names.length >= 2) return names;
    }
    const currentCompany = String(person?.current_company || '').trim();
    if (currentCompany && !seen.has(currentCompany.toLowerCase())) {
      names.push(currentCompany);
    }
    return names.slice(0, 2);
  };

  const buildLinkedInSearchUrl = (name, companyNames = []) => {
    const queryParts = [
      String(name || '').trim(),
      ...companyNames.slice(0, 2).map((c) => String(c || '').trim()).filter(Boolean),
      'linkedin',
    ].filter(Boolean);
    return `https://www.google.com/search?q=${encodeURIComponent(queryParts.join(' '))}`;
  };
  const buildCompanyDetailsUrl = (company, hopValue) => {
    const params = new URLSearchParams();
    if (searchParams.orgId != null) params.set('sourceOrgId', String(searchParams.orgId));
    if (searchParams.companyName) params.set('sourceCompanyName', String(searchParams.companyName));
    if (company?.organizationId != null) params.set('destOrgId', String(company.organizationId));
    if (company?.name) params.set('companyName', String(company.name));
    if (hopValue != null) params.set('hop', String(hopValue));
    if (searchParams.startYear) params.set('startYear', String(searchParams.startYear));
    if (searchParams.endYear) params.set('endYear', String(searchParams.endYear));
    if (contextRole) params.set('role', String(contextRole));
    if (appliedConnectionFilters && typeof appliedConnectionFilters === 'object') {
      params.set('cf', JSON.stringify(appliedConnectionFilters));
    }
    return `/company-details?${params.toString()}`;
  };
  const openCompanyDetailsInNewTab = (company, hopValue) => {
    const url = buildCompanyDetailsUrl(company, hopValue);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    try {
      sessionStorage.setItem(
        connectionFiltersStorageKey,
        JSON.stringify({
          selectedPastCompanies,
          selectedPastRoles,
          selectedTenureOptions,
          selectedColleges,
          selectedDepartments,
          selectedBatchOptions,
          appliedConnectionFilters,
        })
      );
    } catch (_) { }
  }, [
    connectionFiltersStorageKey,
    selectedPastCompanies,
    selectedPastRoles,
    selectedTenureOptions,
    selectedColleges,
    selectedDepartments,
    selectedBatchOptions,
    appliedConnectionFilters,
  ]);

  const buildConnectionFilters = () => {
    const companies = selectedPastCompanies ?? [];
    const roles = (selectedPastRoles ?? []).filter((v) => v !== ANY_ROLE_TOKEN);
    const tenure = selectedTenureOptions ?? [];
    const colleges = selectedColleges ?? [];
    const depts = (selectedDepartments ?? []).filter((v) => v !== ANY_DEPARTMENT_TOKEN);
    const batch = selectedBatchOptions ?? [];
    const hasAny = companies.length > 0 || roles.length > 0 || tenure.length > 0 || colleges.length > 0 || depts.length > 0 || batch.length > 0;
    if (!hasAny) return null;
    return {
      past_companies: companies,
      past_roles: roles,
      tenure_options: tenure,
      colleges,
      departments: depts,
      batch_options: batch,
    };
  };

  const applyFiltersAndClose = () => {
    const nextFilters = buildConnectionFilters();
    setAppliedConnectionFilters(nextFilters);
    if (nextFilters) {
      setConnectionFilterApplyTick((n) => n + 1);
    } else {
      setShowNoMatchHint(false);
    }
    setShowContextMismatchHint(false);
    setIsFilterOpen(false);
  };

  const handleUpdateTopContext = async (e) => {
    e?.preventDefault();

    const company = (topCompanyName || '').trim();
    if (!company) {
      toast.error('Please enter a company name');
      return;
    }
    if (!topStartYear || !topEndYear) {
      toast.error('Please select both start and end years');
      return;
    }
    if (parseInt(topStartYear, 10) > parseInt(topEndYear, 10)) {
      toast.error('Start year cannot be after end year');
      return;
    }

    setUpdatingOrgContext(true);
    try {
      let resolved = selectedOrg;
      // If the user hasn't explicitly picked from suggestions, fall back to search
      if (!resolved) {
        trackCompanySearch(company, false, {
          source: 'dashboard',
          start_year: topStartYear,
          end_year: topEndYear,
        });
        const orgs = await searchOrganizations(company);
        if (!orgs || orgs.length === 0) {
          // Org not in DB: navigate to dashboard with company name so user can request notification
          const state = {
            companyName: company,
            startYear: topStartYear,
            endYear: topEndYear,
            orgNotFound: true,
          };
          try { sessionStorage.setItem(dashboardStateStorageKey, JSON.stringify(state)); } catch (_) { }
          navigate('/dashboard', { state });
          return;
        }
        resolved = orgs[0];
      }
      trackCompanySearch(resolved.name, true, {
        source: 'dashboard',
        org_id: resolved.id,
        start_year: topStartYear,
        end_year: topEndYear,
      });
      const state = {
        orgId: resolved.id,
        companyName: resolved.name,
        startYear: topStartYear,
        endYear: topEndYear,
      };
      try {
        sessionStorage.setItem(dashboardStateStorageKey, JSON.stringify(state));
      } catch (_) { }
      trackCoreFeatureUsed('company_search_from_dashboard', {
        company_name: resolved.name,
        org_id: resolved.id,
        start_year: topStartYear,
        end_year: topEndYear,
      });
      incrementActivationCounter('company_searches');
      navigate('/insights', { state });
    } catch (err) {
      toast.error(err.message || 'Failed to update company and year');
    } finally {
      setUpdatingOrgContext(false);
    }
  };

  const handleSimilarCompanyClick = (company) => {
    const orgId = Number(company?.organization_id);
    const orgName = String(company?.organization || '').trim();
    if (!orgName || Number.isNaN(orgId)) return;

    const nowYear = new Date().getFullYear();
    const startYear = String(nowYear - 2);
    const endYear = String(nowYear);

    // Reflect selection immediately in the search controls.
    setTopCompanyName(orgName);
    setTopStartYear(startYear);
    setTopEndYear(endYear);
    setSelectedOrg({ id: orgId, name: orgName });
    setHasTouchedCompanyInput(true);

    const state = { orgId, companyName: orgName, startYear, endYear };
    try {
      sessionStorage.setItem(dashboardStateStorageKey, JSON.stringify(state));
    } catch (_) { }

    incrementActivationCounter('similar_company_clicks', false);
    trackCoreFeatureUsed('similar_company_clicked', { company_name: orgName, dest_org_id: orgId });
    navigate('/insights', { state });
  };

  // Fetch profile to know if filters can be shown and to build company/role lists
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoaded(true);
      return;
    }
    setProfileLoaded(false);
    getProfile()
      .then((data) => {
        setProfile(data);
        setProfileLoaded(true);
      })
      .catch(() => {
        setProfile(null);
        setProfileLoaded(true);
      });
  }, [user]);

  // Debounced company search for suggestions (get_org / organizations API)
  useEffect(() => {
    const q = (topCompanyName || '').trim();
    // Only show suggestions after the user has interacted with the field
    if (!hasTouchedCompanyInput) {
      setOrgSuggestions([]);
      setShowOrgSuggestions(false);
      return;
    }
    if (!q || q.length < 3) {
      setOrgSuggestions([]);
      setShowOrgSuggestions(false);
      return;
    }
    const t = setTimeout(() => {
      setOrgSuggestionsLoading(true);
      searchOrganizations(q)
        .then((list) => {
          setOrgSuggestions(Array.isArray(list) ? list : []);
          setShowOrgSuggestions(true);
        })
        .catch(() => {
          setOrgSuggestions([]);
        })
        .finally(() => setOrgSuggestionsLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [topCompanyName, hasTouchedCompanyInput]);

  // Fetch org-transitions when we have orgId and date range (after profile has loaded to avoid duplicate fetches)
  useEffect(() => {
    const orgId = searchParams.orgId;
    const startYear = searchParams.startYear;
    const endYear = searchParams.endYear;

    if (authLoading) return;

    if (!user) {
      openLogin();
      return;
    }

    if (!orgId) {
      setCompaniesFirst([]);
      setCompaniesSecond([]);
      setCompaniesThird([]);
      setTotalAlumni(0);
      setLoading(false);
      // If the org was not found in DB, stay on dashboard to show the "no data yet" banner
      if (searchParams.orgNotFound) {
        setError(null);
        return;
      }
      setError('Please select an organization to view insights');
      // Proactively navigate home if no context
      if (!location.state) navigate('/');
      return;
    }

    const startDate = startYear ? `${startYear}-01-01` : null;
    const endDate = endYear ? `${endYear}-12-31` : null;
    const useDashboardData = !appliedConnectionFilters;
    const filtersKey = appliedConnectionFilters ? JSON.stringify(appliedConnectionFilters) : 'none';
    const fetchKey = `${orgId}-${startYear}-${endYear}-${contextRole || ''}-${filtersKey}-${useDashboardData ? 'd' : 't'}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    const requestSeq = ++transitionsRequestSeqRef.current;

    setLoading(true);
    setError(null);

    if (useDashboardData) {
      getDashboardData(orgId, { startDate, endDate, hops: 3, role: contextRole })
        .then((data) => {
          if (requestSeq !== transitionsRequestSeqRef.current) return;
          const transitions = data.transitions || {};
          const relatedByDestData = (typeof data.related_by_dest === 'object' && data.related_by_dest !== null)
            ? data.related_by_dest : {};
          const { firstHopCompanies, secondHopCompanies, thirdHopCompanies } = mapTransitions(transitions, relatedByDestData);
          const first = firstHopCompanies ?? [];
          const second = secondHopCompanies ?? [];
          const third = thirdHopCompanies ?? [];
          setCompaniesFirst(first);
          setCompaniesSecond(second);
          setCompaniesThird(third);
          const total = Number(data.total_alumni ?? transitions.total_alumni ?? first.reduce((sum, c) => sum + (c.people || 0), 0));
          setTotalAlumni(total);
          setEmployeesTotal(Number(data.total_employees ?? 0));
          setAlumni(Array.isArray(data.alumni) ? data.alumni : []);
          const highlighted = new Set();
          if (transitions && typeof transitions === 'object') {
            Object.values(transitions).forEach((list) => {
              if (!Array.isArray(list)) return;
              list.forEach((item) => {
                if (!item || item.organization_id == null) return;
                if (item.role_match || item.match) highlighted.add(item.organization_id);
              });
            });
          }
          setHighlightedOrgIds(highlighted);
          setRelatedByDest(relatedByDestData);
          trackCoreFeatureUsed('hiring_pattern_view', { org_id: orgId, start_year: startYear, end_year: endYear, has_role_filter: !!contextRole });
        })
        .catch((err) => {
          if (requestSeq !== transitionsRequestSeqRef.current) return;
          setError(err.message || 'Failed to load dashboard data');
          setCompaniesFirst([]);
          setCompaniesSecond([]);
          setCompaniesThird([]);
          setTotalAlumni(0);
          setEmployeesTotal(0);
          setAlumni([]);
          setRelatedByDest({});
        })
        .finally(() => {
          if (requestSeq !== transitionsRequestSeqRef.current) return;
          setLoading(false);
        });
      return;
    }

    getOrgTransitions(orgId, { startDate, endDate, hops: 3, role: contextRole, connectionFilters: appliedConnectionFilters, includeRelated: true, includeRelatedDetails: false })
      .then((data) => {
        if (requestSeq !== transitionsRequestSeqRef.current) return;
        const relatedByDestData = (typeof data.related_by_dest === 'object' && data.related_by_dest !== null)
          ? data.related_by_dest : {};
        const { firstHopCompanies, secondHopCompanies, thirdHopCompanies } = mapTransitions(data, relatedByDestData);
        const first = firstHopCompanies ?? [];
        const second = secondHopCompanies ?? [];
        const third = thirdHopCompanies ?? [];
        setCompaniesFirst(first);
        setCompaniesSecond(second);
        setCompaniesThird(third);
        const total = Number(data.total_alumni ?? first.reduce((sum, c) => sum + (c.people || 0), 0));
        setTotalAlumni(total);
        setEmployeesTotal(Number(data.total_employees ?? 0));
        const highlighted = new Set();
        if (data && typeof data === 'object') {
          Object.values(data).forEach((list) => {
            if (!Array.isArray(list)) return;
            list.forEach((item) => {
              if (!item || item.organization_id == null) return;
              const hasMatchFlag = item.role_match || item.match;
              if (hasMatchFlag) highlighted.add(item.organization_id);
            });
          });
        }
        setHighlightedOrgIds(highlighted);
        setRelatedByDest(relatedByDestData);
        trackCoreFeatureUsed('hiring_pattern_view', { org_id: orgId, start_year: startYear, end_year: endYear, has_role_filter: !!contextRole });
      })
      .catch((err) => {
        if (requestSeq !== transitionsRequestSeqRef.current) return;
        setError(err.message || 'Failed to load transitions');
        setCompaniesFirst([]);
        setCompaniesSecond([]);
        setCompaniesThird([]);
        setTotalAlumni(0);
        setEmployeesTotal(0);
        setRelatedByDest({});
      })
      .finally(() => {
        if (requestSeq !== transitionsRequestSeqRef.current) return;
        setLoading(false);
      });
  }, [searchParams.orgId, searchParams.startYear, searchParams.endYear, searchParams.orgNotFound, contextRole, appliedConnectionFilters, user, openLogin, authLoading, location.state, navigate]);

  const getAlumniCacheKey = useCallback(() => {
    const orgId = searchParams.orgId || '';
    const start = searchParams.startYear || '';
    const end = searchParams.endYear || '';
    const filtersKey = appliedConnectionFilters ? JSON.stringify(appliedConnectionFilters) : 'none';
    return `${orgId}-${start}-${end}-${filtersKey}`;
  }, [searchParams.orgId, searchParams.startYear, searchParams.endYear, appliedConnectionFilters]);

  const getEmployeesCacheKey = useCallback(() => {
    const orgId = searchParams.orgId || '';
    const start = searchParams.startYear || '';
    const end = searchParams.endYear || '';
    const filtersKey = appliedConnectionFilters ? JSON.stringify(appliedConnectionFilters) : 'none';
    return `${orgId}-${start}-${end}-${filtersKey}`;
  }, [searchParams.orgId, searchParams.startYear, searchParams.endYear, appliedConnectionFilters]);

  // Fetch alumni when the tab is active
  useEffect(() => {
    if (activeTab !== 'alumni' || !searchParams.orgId) return;

    const cacheKey = getAlumniCacheKey();
    const cached = alumniCacheRef.current.get(cacheKey);
    if (cached && Array.isArray(cached.items)) {
      setAlumni(cached.items);
      setAlumniPage(cached.page || 1);
      setAlumniTotal(Number(cached.total ?? cached.items.length));
      return;
    }

    setLoadingAlumni(true);
    const startDate = searchParams.startYear ? `${searchParams.startYear}-01-01` : null;
    const endDate = searchParams.endYear ? `${searchParams.endYear}-12-31` : null;

    const page = 1;
    setAlumniPage(page);
    getAlumni(searchParams.orgId, { startDate, endDate, connectionFilters: appliedConnectionFilters, page, limit: 20 })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setAlumni(items);
        const total = Number(data?.total ?? items.length);
        setAlumniTotal(total);
        alumniCacheRef.current.set(cacheKey, { items, page, total });
      })
      .catch((err) => {
        console.error('Failed to load alumni', err);
      })
      .finally(() => setLoadingAlumni(false));
  }, [activeTab, searchParams.orgId, searchParams.startYear, searchParams.endYear, appliedConnectionFilters, getAlumniCacheKey]);

  // Fetch current employees when the tab is active
  useEffect(() => {
    if (activeTab !== 'employees' || !searchParams.orgId) return;

    const cacheKey = getEmployeesCacheKey();
    const cached = employeesCacheRef.current.get(cacheKey);
    if (cached && Array.isArray(cached.items)) {
      setEmployees(cached.items);
      setEmployeesPage(cached.page || 1);
      setEmployeesTotal(Number(cached.total ?? cached.items.length));
      setEmployeesFetched(true);
      return;
    }

    setLoadingEmployees(true);
    const startDate = searchParams.startYear ? `${searchParams.startYear}-01-01` : null;
    const endDate = searchParams.endYear ? `${searchParams.endYear}-12-31` : null;
    const page = 1;
    setEmployeesPage(page);
    getCurrentEmployees(searchParams.orgId, { startDate, endDate, connectionFilters: appliedConnectionFilters, page, limit: 20 })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setEmployees(items);
        const total = Number(data?.total ?? items.length);
        setEmployeesTotal(total);
        setEmployeesFetched(true);
        employeesCacheRef.current.set(cacheKey, { items, page, total });
      })
      .catch((err) => {
        console.error('Failed to load employees', err);
      })
      .finally(() => setLoadingEmployees(false));
  }, [activeTab, searchParams.orgId, searchParams.startYear, searchParams.endYear, appliedConnectionFilters, getEmployeesCacheKey]);

  // Base client-side filters that should always apply.
  const applyCommonFilters = (list) => {
    let next = Array.isArray(list) ? list : [];
    if ((searchName || '').trim()) {
      const q = (searchName || '').toLowerCase();
      next = next.filter((c) => c && String(c.name || '').toLowerCase().includes(q));
    }
    // Min/Max transitions filter using totalPeople as a proxy
    const minVal = minTransitions !== '' ? Number(minTransitions) : null;
    const maxVal = maxTransitions !== '' ? Number(maxTransitions) : null;
    if (minVal != null && !Number.isNaN(minVal)) {
      next = next.filter((c) => (c.totalPeople || c.people || 0) >= minVal);
    }
    if (maxVal != null && !Number.isNaN(maxVal)) {
      next = next.filter((c) => (c.totalPeople || c.people || 0) <= maxVal);
    }
    return next;
  };
  const applyYearFilter = (list) => {
    if (selectedYear === 'all') return Array.isArray(list) ? list : [];
    const yearNum = Number(selectedYear);
    return (Array.isArray(list) ? list : []).filter((c) => (c.years || []).includes(yearNum));
  };

  // When a role is set, only show companies that hired into that role (role_match from API)
  const applyRoleFilter = (list) => {
    const role = (contextRole || '').trim();
    if (!role) return Array.isArray(list) ? list : [];
    return (Array.isArray(list) ? list : []).filter((c) => highlightedOrgIds.has(c.organizationId));
  };
  const isConnectionFilterActive = !!appliedConnectionFilters;

  const loadMoreAlumni = () => {
    if (!searchParams.orgId || alumniLoadingMore || !alumniHasMore) return;
    const nextPage = alumniPage + 1;
    setAlumniLoadingMore(true);
    const startDate = searchParams.startYear ? `${searchParams.startYear}-01-01` : null;
    const endDate = searchParams.endYear ? `${searchParams.endYear}-12-31` : null;
    const cacheKey = getAlumniCacheKey();
    getAlumni(searchParams.orgId, { startDate, endDate, connectionFilters: appliedConnectionFilters, page: nextPage, limit: 20 })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setAlumni(prev => {
          const merged = [...(prev || []), ...items];
          alumniCacheRef.current.set(cacheKey, { items: merged, page: nextPage, total: Number(data?.total ?? alumniTotal) });
          return merged;
        });
        setAlumniPage(nextPage);
        setAlumniTotal(Number(data?.total ?? alumniTotal));
      })
      .catch((err) => {
        console.error('Failed to load more alumni', err);
        toast.error(err.message || 'Failed to load more alumni');
      })
      .finally(() => setAlumniLoadingMore(false));
  };

  const loadMoreEmployees = () => {
    if (!searchParams.orgId || employeesLoadingMore || !employeesHasMore) return;
    const nextPage = employeesPage + 1;
    setEmployeesLoadingMore(true);
    const startDate = searchParams.startYear ? `${searchParams.startYear}-01-01` : null;
    const endDate = searchParams.endYear ? `${searchParams.endYear}-12-31` : null;
    const cacheKey = getEmployeesCacheKey();
    getCurrentEmployees(searchParams.orgId, { startDate, endDate, connectionFilters: appliedConnectionFilters, page: nextPage, limit: 20 })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setEmployees(prev => {
          const merged = [...(prev || []), ...items];
          employeesCacheRef.current.set(cacheKey, { items: merged, page: nextPage, total: Number(data?.total ?? employeesTotal) });
          return merged;
        });
        setEmployeesPage(nextPage);
        setEmployeesTotal(Number(data?.total ?? employeesTotal));
      })
      .catch((err) => {
        console.error('Failed to load more employees', err);
        toast.error(err.message || 'Failed to load more employees');
      })
      .finally(() => setEmployeesLoadingMore(false));
  };

  const prioritizeFilterMatches = (list) => {
    const base = Array.isArray(list) ? [...list] : [];
    if (!isConnectionFilterActive) return base;
    base.sort((a, b) =>
      Number((b.filterMatchCount || 0) > 0) - Number((a.filterMatchCount || 0) > 0) ||
      (b.filterMatchCount || 0) - (a.filterMatchCount || 0) ||
      (b.relatedCount || 0) - (a.relatedCount || 0) ||
      (a.rank || 0) - (b.rank || 0)
    );
    return base.map((c, idx) => ({ ...c, rank: idx + 1 }));
  };

  const baseCompaniesFirst = prioritizeFilterMatches(applyCommonFilters(companiesFirst ?? []));
  const baseCompaniesSecond = prioritizeFilterMatches(applyCommonFilters(companiesSecond ?? []));
  const baseCompaniesThird = prioritizeFilterMatches(applyCommonFilters(companiesThird ?? []));

  const strictCompaniesFirst = applyRoleFilter(applyYearFilter(baseCompaniesFirst));
  const strictCompaniesSecond = applyRoleFilter(applyYearFilter(baseCompaniesSecond));
  const strictCompaniesThird = applyRoleFilter(applyYearFilter(baseCompaniesThird));

  const strictFilteredCompaniesFirst = useMemo(
    () => ((selectedTransition === 'all' || selectedTransition === '1') ? strictCompaniesFirst : []),
    [selectedTransition, strictCompaniesFirst]
  );
  const strictFilteredCompaniesSecond = useMemo(
    () => ((selectedTransition === 'all' || selectedTransition === '2') ? strictCompaniesSecond : []),
    [selectedTransition, strictCompaniesSecond]
  );
  const strictFilteredCompaniesThird = useMemo(
    () => ((selectedTransition === 'all' || selectedTransition === '3+') ? strictCompaniesThird : []),
    [selectedTransition, strictCompaniesThird]
  );
  const strictFilteredTotalCount = (strictFilteredCompaniesFirst?.length || 0)
    + (strictFilteredCompaniesSecond?.length || 0)
    + (strictFilteredCompaniesThird?.length || 0);
  const hasContextMismatchFilters = selectedTransition !== 'all' || selectedYear !== 'all' || String(contextRole || '').trim() !== '';
  const destinationCompaniesCount = Number(searchParams.destinationCompaniesCount || 0);
  const hasInsufficientDestinationCompanies = Boolean(searchParams.insufficientDestinations)
    || (Number.isFinite(destinationCompaniesCount) && destinationCompaniesCount > 0 && destinationCompaniesCount < 5);
  const hasRawPathwaysData = (companiesFirst?.length || 0) > 0
    || (companiesSecond?.length || 0) > 0
    || (companiesThird?.length || 0) > 0;
  const shouldFallbackToBaseCompanies = hasRawPathwaysData && hasContextMismatchFilters && strictFilteredTotalCount === 0;

  const filteredCompaniesFirst = shouldFallbackToBaseCompanies
    ? baseCompaniesFirst
    : strictFilteredCompaniesFirst;
  const filteredCompaniesSecond = shouldFallbackToBaseCompanies
    ? baseCompaniesSecond
    : strictFilteredCompaniesSecond;
  const filteredCompaniesThird = shouldFallbackToBaseCompanies
    ? baseCompaniesThird
    : strictFilteredCompaniesThird;

  const showFirstSection = shouldFallbackToBaseCompanies || selectedTransition === 'all' || selectedTransition === '1';
  const hasVisibleFirstSection = showFirstSection && (filteredCompaniesFirst ?? []).length > 0;
  const hasVisibleSecondSection = (filteredCompaniesSecond ?? []).length > 0;
  const hasCompanyFiltersActive = Boolean(
    String(searchName || '').trim() ||
    selectedTransition !== 'all' ||
    selectedYear !== 'all' ||
    String(minTransitions || '').trim() ||
    String(maxTransitions || '').trim() ||
    String(contextRole || '').trim() ||
    isConnectionFilterActive
  );
  const hasAlumniFiltersActive = Boolean(String(searchName || '').trim() || isConnectionFilterActive);
  const hasEmployeesFiltersActive = Boolean(String(searchName || '').trim() || isConnectionFilterActive);
  const companyNoMatchMessage = isConnectionFilterActive
    ? 'No results matched your current connection filters. Try broadening company, role, tenure, college, or batch filters.'
    : hasInsufficientDestinationCompanies
      ? `Only ${destinationCompaniesCount || 0} destination companies found even after expanding tenure.`
      : hasCompanyFiltersActive
        ? 'Nothing matched your current filters. Try using new filters to broaden your results.'
        : 'No company pathways found for this period.';
  const alumniNoMatchMessage = isConnectionFilterActive
    ? 'No alumni matched your current connection filters. Try broadening company, role, tenure, college, or batch filters.'
    : hasAlumniFiltersActive
      ? 'Nothing matched your current filters. Try using new filters to find alumni.'
      : 'No alumni data found for this period.';
  const employeesNoMatchMessage = isConnectionFilterActive
    ? 'No employees matched your current connection filters. Try broadening company, role, tenure, college, or batch filters.'
    : hasEmployeesFiltersActive
      ? 'Nothing matched your current filters. Try using new filters to find employees.'
      : 'No current employees found for this company.';
  const appliedFiltersCount = useMemo(() => {
    if (!appliedConnectionFilters || typeof appliedConnectionFilters !== 'object') return 0;
    const keys = ['past_companies', 'past_roles', 'tenure_options', 'colleges', 'departments', 'batch_options'];
    return keys.reduce((acc, k) => {
      const v = appliedConnectionFilters[k];
      return acc + (Array.isArray(v) ? v.length : 0);
    }, 0);
  }, [appliedConnectionFilters]);

  const matchedCompaniesCount = useMemo(() => {
    if (!isConnectionFilterActive) return 0;
    const matched = new Set();
    [...filteredCompaniesFirst, ...filteredCompaniesSecond, ...filteredCompaniesThird].forEach((c) => {
      if ((c?.filterMatchCount || 0) > 0 && c?.organizationId != null) matched.add(c.organizationId);
    });
    return matched.size;
  }, [isConnectionFilterActive, filteredCompaniesFirst, filteredCompaniesSecond, filteredCompaniesThird]);
  const totalVisibleCompaniesCount = useMemo(() => {
    const visible = new Set();
    [...filteredCompaniesFirst, ...filteredCompaniesSecond, ...filteredCompaniesThird].forEach((c) => {
      if (c?.organizationId != null) visible.add(c.organizationId);
    });
    return visible.size;
  }, [filteredCompaniesFirst, filteredCompaniesSecond, filteredCompaniesThird]);
  const isNoPathwaysData = !loading
    && !error
    && (
      hasInsufficientDestinationCompanies
      || (
        (filteredCompaniesFirst ?? []).length === 0
        && (filteredCompaniesSecond ?? []).length === 0
        && (filteredCompaniesThird ?? []).length === 0
      )
    );
  const shouldShowNoDataRecoverySections = !hasCompanyFiltersActive
    && (!hasRawPathwaysData || !!searchParams.orgNotFound || hasInsufficientDestinationCompanies);

  useEffect(() => {
    if (activeTab !== 'company-pathways') return;
    if (!isNoPathwaysData) return;
    if (!shouldShowNoDataRecoverySections) return;
    if (!user) return;
    let cancelled = false;
    setSimilarCompaniesLoading(true);
    getSimilarCompanies({
      orgId: searchParams.orgId,
      orgName: searchParams.companyName || '',
      limit: 10,
    })
      .then((list) => {
        if (cancelled) return;
        setSimilarCompanies(Array.isArray(list) ? list.slice(0, 10) : []);
      })
      .catch(() => {
        if (!cancelled) setSimilarCompanies([]);
      })
      .finally(() => {
        if (!cancelled) setSimilarCompaniesLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, isNoPathwaysData, shouldShowNoDataRecoverySections, user, searchParams.orgId, searchParams.companyName]);

  useEffect(() => {
    if (activeTab === 'company-pathways' && shouldShowNoDataRecoverySections && !loading && !error) {
      trackCoreFeatureUsed('no_data_recovery_viewed', {
        org_id: searchParams.orgId,
        company_name: searchParams.companyName,
        is_not_found: !!searchParams.orgNotFound
      });
    }
  }, [activeTab, shouldShowNoDataRecoverySections, loading, error, searchParams.orgId, searchParams.companyName, searchParams.orgNotFound]);

  useEffect(() => {
    if (!connectionFilterApplyTick) return;
    if (loading) return;
    if (!isConnectionFilterActive) return;
    if (matchedCompaniesCount > 0) return;
    setShowNoMatchHint(true);
    const timer = setTimeout(() => setShowNoMatchHint(false), 4000);
    return () => clearTimeout(timer);
  }, [connectionFilterApplyTick, loading, isConnectionFilterActive, matchedCompaniesCount]);

  useEffect(() => {
    if (activeTab !== 'company-pathways') return;
    if (loading || error) return;
    if (!shouldFallbackToBaseCompanies) return;
    setShowContextMismatchHint(true);
    const timer = setTimeout(() => setShowContextMismatchHint(false), 5000);
    return () => clearTimeout(timer);
  }, [activeTab, loading, error, shouldFallbackToBaseCompanies, selectedTransition, selectedYear, contextRole]);

  useEffect(() => {
    if (!showNoMatchHint && !showContextMismatchHint) return;
    const dismiss = () => {
      setShowNoMatchHint(false);
      setShowContextMismatchHint(false);
    };
    const opts = { passive: true };
    window.addEventListener('click', dismiss, opts);
    window.addEventListener('keydown', dismiss);
    window.addEventListener('input', dismiss, opts);
    window.addEventListener('scroll', dismiss, opts);
    window.addEventListener('touchstart', dismiss, opts);
    return () => {
      window.removeEventListener('click', dismiss);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('input', dismiss);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchstart', dismiss);
    };
  }, [showNoMatchHint, showContextMismatchHint]);

  const scrollToTransitions = (which) => {
    const ref = which === 1 ? firstTransitionsRef : which === 2 ? secondTransitionsRef : thirdTransitionsRef;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // Reset per-account so each user gets their own first-visit behavior.
    hasAutoShownTourRef.current = false;
  }, [user?.id, user?.email]);

  useEffect(() => {
    if (hasAutoShownTourRef.current || authLoading || !user) return;
    if (location.pathname !== '/dashboard') return;
    try {
      const seen = localStorage.getItem(tourSeenStorageKey);
      if (seen === '1') return;
    } catch (_) { }
    const timer = setTimeout(() => {
      hasAutoShownTourRef.current = true;
      setTourOpen(true);
      setTourStep(0);
    }, 5000);
    return () => clearTimeout(timer);
  }, [authLoading, user, location.pathname, tourSeenStorageKey]);

  useEffect(() => {
    if (!tourOpen) return;
    const step = DASHBOARD_TOUR_STEPS[tourStep];
    if (!step) return;
    if (['company-card', 'people-first', 'people-other', 'recent', 'years-list'].includes(step.key) && activeTab !== 'company-pathways') {
      setActiveTab('company-pathways');
      return;
    }
    const t = setTimeout(() => {
      const node = document.querySelector(`[data-tour="${step.key}"]`);
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [tourOpen, tourStep, activeTab]);

  useEffect(() => {
    if (!tourOpen) return;

    const updatePanelPosition = () => {
      const step = DASHBOARD_TOUR_STEPS[tourStep];
      const node = step ? document.querySelector(`[data-tour="${step.key}"]`) : null;
      const panelWidth = Math.min(420, Math.max(260, window.innerWidth - 32));

      if (!node || typeof node.getBoundingClientRect !== 'function') {
        setTourPanelPos({ top: 24, left: Math.max(16, window.innerWidth - panelWidth - 16), width: panelWidth });
        return;
      }

      const rect = node.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const gap = 14;
      const panelH = 220;

      let left = rect.right + gap;
      if (left + panelWidth > viewportW - 16) {
        left = rect.left - panelWidth - gap;
      }
      if (left < 16) {
        left = Math.max(16, viewportW - panelWidth - 16);
      }

      let top = rect.top + (rect.height / 2) - (panelH / 2);
      if (top < 16) top = 16;
      if (top + panelH > viewportH - 16) top = viewportH - panelH - 16;

      setTourPanelPos({ top, left, width: panelWidth });
    };

    const t = setTimeout(updatePanelPosition, 140);
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [tourOpen, tourStep, activeTab, filteredCompaniesFirst]);

  const isTourTarget = (key) => tourOpen && DASHBOARD_TOUR_STEPS[tourStep]?.key === key;
  const sourceCompany = (topCompanyName || searchParams.companyName || 'company b').trim();
  const targetCompany = String(filteredCompaniesFirst?.[0]?.name || 'company a').trim();
  const recentYearForTour = String(filteredCompaniesFirst?.[0]?.recent || 'year');
  const escapeRegex = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const renderHighlightedTourText = (value) => {
    const text = String(value || '');
    const tokens = [sourceCompany, targetCompany, recentYearForTour]
      .map((t) => String(t || '').trim())
      .filter(Boolean);
    const uniqueTokens = Array.from(new Set(tokens));
    const tokenPattern = [
      ...uniqueTokens
        .sort((a, b) => b.length - a.length)
        .map((t) => escapeRegex(t)),
      '\\b(?:19|20)\\d{2}\\b',
    ].join('|');
    if (!tokenPattern) return text;
    const regex = new RegExp(`(${tokenPattern})`, 'gi');
    return text.split(regex).map((part, idx) => {
      if (!part) return null;
      const normalized = part.toLowerCase();
      const isToken = uniqueTokens.some((t) => t.toLowerCase() === normalized);
      const isYear = /\b(?:19|20)\d{2}\b/.test(part);
      if (isToken || isYear) {
        return (
          <span
            key={`${part}-${idx}`}
            className="inline-flex items-center rounded-md bg-[#FEF3C7] text-[#92400E] font-semibold px-1.5 py-0.5 mx-0.5"
          >
            {part}
          </span>
        );
      }
      return <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>;
    });
  };
  const getTourDescription = () => {
    const raw = String(DASHBOARD_TOUR_STEPS[tourStep]?.description || '');
    return raw
      .replaceAll('{{company b}}', sourceCompany)
      .replaceAll('{{company a}}', targetCompany)
      .replaceAll('{{year}}', recentYearForTour);
  };
  const startTour = () => {
    setTourOpen(true);
    setTourStep(0);
  };
  const closeTour = () => {
    setTourOpen(false);
    setTourStep(0);
    try {
      localStorage.setItem(tourSeenStorageKey, '1');
    } catch (_) { }
  };
  const nextTourStep = () => {
    if (tourStep >= DASHBOARD_TOUR_STEPS.length - 1) {
      closeTour();
      return;
    }
    setTourStep((prev) => prev + 1);
  };
  const prevTourStep = () => setTourStep((prev) => Math.max(0, prev - 1));

  const isOrgHighlighted = (orgId) => highlightedOrgIds.has(orgId);
  const buildCardSelectionKey = (hop, company, index) => `${hop}:${company?.organizationId ?? company?.name ?? index}`;
  const isCardSelected = (hop, company, index) => selectedCards.has(buildCardSelectionKey(hop, company, index));
  const toggleCardSelected = (hop, company, index) => {
    const key = buildCardSelectionKey(hop, company, index);
    const name = String(company?.name || '').trim();
    setSelectedCards((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, name);
      return next;
    });
  };
  const clearSelectedCards = () => setSelectedCards(new Map());
  const selectedCompanyNames = useMemo(() => {
    const names = [];
    const seen = new Set();
    for (const value of selectedCards.values()) {
      const n = String(value || '').trim();
      const k = n.toLowerCase();
      if (!n || seen.has(k)) continue;
      seen.add(k);
      names.push(n);
    }
    return names;
  }, [selectedCards]);
  const selectedCount = selectedCards.size;
  const isSelectionMode = selectedCount > 0;
  const selectedPreview = selectedCompanyNames.slice(0, 3).join(', ');
  const selectedOverflow = Math.max(0, selectedCompanyNames.length - 3);
  const selectedSummaryText = selectedPreview
    ? `${selectedPreview}${selectedOverflow > 0 ? ` +${selectedOverflow} more` : ''}`
    : `${selectedCount} selected`;
  const buildKeywordsFromRoles = (roles = []) => {
    const tags = (Array.isArray(roles) ? roles : [])
      .map((r) => String(r || '').trim())
      .filter(Boolean);
    if (tags.length === 0) return '';
    if (tags.length === 1) return tags[0];
    return tags.join(' OR ');
  };
  const openLinkedInJobsWithAutoRefresh = (url) => {
    // Open directly to the URL for the first "render"
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) return;

    // "Double render" by hitting it again/reloading periodically
    // LinkedIn often shows "no jobs" on the first hit but works on second
    [800, 2200, 4500].forEach((delayMs) => {
      window.setTimeout(() => {
        try {
          if (win.closed) return;
          // Try setting href again to force a 're-render'
          win.location.href = url;
        } catch (_) {
          // Fallback to reload if already on same origin (unlikely for LinkedIn)
          try { win.location.reload(); } catch (__) { }
        }
      }, delayMs);
    });
  };
  const openLinkedInJobsForSelection = async (roles = []) => {
    const tags = (Array.isArray(roles) ? roles : [])
      .map((r) => String(r || '').trim())
      .filter(Boolean);
    const keywords = buildKeywordsFromRoles(tags);
    trackFindingJobs({
      source: 'dashboard',
      action: 'open_linkedin',
      selected_company_count: selectedCompanyNames.length,
      selected_company_names: selectedCompanyNames.slice(0, 20),
      roles: tags.slice(0, 20),
      keywords: keywords || undefined,
    });
    try {
      const data = await getLinkedinOrgIdsByCompanyNames(selectedCompanyNames);
      const ids = Array.isArray(data?.linkedin_org_ids)
        ? data.linkedin_org_ids
          .map((v) => String(v || '').trim())
          .filter((v) => /^\d+$/.test(v))
        : [];

      const params = new URLSearchParams();
      if (keywords) params.set('keywords', keywords);
      params.set('f_TPR', 'r2592000');
      if (ids.length > 0) params.set('f_C', ids.join(','));
      const q = params.toString();
      const url = `https://www.linkedin.com/jobs/search-results/${q ? `?${q}` : ''}`;
      openLinkedInJobsWithAutoRefresh(url);
      setIsRoleModalOpen(false);
      setRoleInput('');
    } catch (err) {
      toast.error(err.message || 'Could not open LinkedIn jobs');
    }
  };

  const buildShareContent = (referralLink) => {
    const senderName = (user?.name || 'A colleague').trim();
    const linkedInMessage = `I just came across something that connects you to the right people and opportunities already within your network. It immediately made me think of you You might want to check this once: ${referralLink}`;
    const whatsappMessage = `Just saw something that actually shows who in your network can open real opportunities for you. Thought of you instantly. Check this once: ${referralLink}`;
    const gmailSubject = 'Thought this would interest you';
    const gmailBody = `Hi,\n\nI came across a platform that uncovers hidden opportunities already present within your professional network.\n\nWhy this stood out:\n- Shows transition patterns from real career paths\n- Surfaces relevant network signals\n- Helps discover opportunities you might otherwise miss\n\nTake a look here:\n${referralLink}\n\nWould love to hear what you think.\n\nBest,\n${senderName}`;
    return { linkedInMessage, whatsappMessage, gmailSubject, gmailBody };
  };

  const openReferralShare = async (channel, referralLink) => {
    if (!user) { openLogin(); return; }
    try {
      await addReferral(referralLink);
      incrementActivationCounter(`referral_${channel}_clicks`, false);
      trackCoreFeatureUsed('referral_share_clicked', { channel, referral_link: referralLink });
    } catch {
      // Non-blocking: share should still open even if tracking fails.
    }

    const { linkedInMessage, whatsappMessage, gmailSubject, gmailBody } = buildShareContent(referralLink);
    let shareUrl = '';
    if (channel === 'whatsapp') {
      shareUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
    } else if (channel === 'gmail') {
      shareUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&su=${encodeURIComponent(gmailSubject)}&body=${encodeURIComponent(gmailBody)}`;
    } else if (channel === 'linkedin') {
      try {
        navigator.clipboard?.writeText(linkedInMessage);
        toast.success('LinkedIn message copied. Paste it in chat after selecting recipients.');
      } catch {
        // ignore clipboard failures
      }
      // LinkedIn web often ignores prefill params; keep clipboard fallback above.
      shareUrl = `https://www.linkedin.com/messaging/compose/?body=${encodeURIComponent(linkedInMessage)}`;
    }
    if (!shareUrl) return;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  // Filter alumni list
  const filteredAlumni = useMemo(() => {
    let next = Array.isArray(alumni) ? alumni : [];
    if ((searchName || '').trim()) {
      next = next.filter((a) => (a && a.name && String(a.name).toLowerCase().includes((searchName || '').toLowerCase())));
    }
    return next;
  }, [alumni, searchName]);

  // Filter employees list
  const filteredEmployees = useMemo(() => {
    let next = Array.isArray(employees) ? employees : [];
    if ((searchName || '').trim()) {
      next = next.filter((a) => (a && a.name && String(a.name).toLowerCase().includes((searchName || '').toLowerCase())));
    }
    return next;
  }, [employees, searchName]);

  const applyRoleContext = async () => {
    const role = (contextRole || '').trim();

    if (role && searchParams.orgId) {
      incrementActivationCounter('role_filters_applied');
    }

    // Save role to session storage for persistence
    try {
      const current = JSON.parse(sessionStorage.getItem(dashboardStateStorageKey) || '{}');
      const updated = { ...current, role };
      sessionStorage.setItem(dashboardStateStorageKey, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save state', e);
    }

    if (!role || !searchParams.orgId) {
      // If there is no role or org context, just clear any highlights.
      setHighlightedOrgIds(new Set());
      return;
    }

    const orgId = searchParams.orgId;
    const startDate = searchParams.startYear ? `${searchParams.startYear}-01-01` : null;
    const endDate = searchParams.endYear ? `${searchParams.endYear}-12-31` : null;

    setContextApplying(true);
    try {
      // Re-fetch org transitions with the role filter so the backend can
      // tell us which destination companies hired into similar roles.
      const data = await getOrgTransitions(orgId, {
        startDate,
        endDate,
        hops: 3,
        role,
      });

      const { firstHopCompanies, secondHopCompanies, thirdHopCompanies } = mapTransitions(data);
      const first = firstHopCompanies ?? [];
      const second = secondHopCompanies ?? [];
      const third = thirdHopCompanies ?? [];
      setCompaniesFirst(first);
      setCompaniesSecond(second);
      setCompaniesThird(third);

      // Build a highlight set from any hop where the backend indicates
      // a role match (supports both `role_match` and `match` flags).
      const highlighted = new Set();
      if (data && typeof data === 'object') {
        Object.values(data).forEach((list) => {
          if (!Array.isArray(list)) return;
          list.forEach((item) => {
            if (!item || item.organization_id == null) return;
            const hasMatchFlag = item.role_match || item.match;
            if (hasMatchFlag) highlighted.add(item.organization_id);
          });
        });
      }

      setHighlightedOrgIds(highlighted);
    } finally {
      setContextApplying(false);
    }
  };

  const { isDark } = useTheme();
  const appCls = isDark ? 'dark-app' : 'light-app';
  const cardCls = isDark ? 'dark-card' : 'light-card';
  const tabCls = isDark ? 'dark-tab' : 'light-tab';
  const pillCls = isDark ? 'dark-pill' : 'light-pill';
  // Contextual colours
  const bgPage = isDark ? 'rgba(13,16,25,0.92)' : 'rgba(238,242,251,0.95)';
  const bgPageSolid = isDark ? '#0d1019' : '#eef2fb';
  const bgSub = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(59,130,246,0.04)';
  const borderSub = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.12)';
  const bgCard = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const borderCard = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const bgFilter = isDark ? 'rgba(13,16,25,0.95)' : 'rgba(238,242,251,0.97)';
  const txtMuted = isDark ? 'rgba(255,255,255,0.38)' : '#64748b';
  const txtBody = isDark ? 'rgba(255,255,255,0.82)' : '#1e293b';
  const txtDim = isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const inputBg = isDark ? '#0d1019' : '#ffffff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1';
  const inputText = isDark ? 'rgba(255,255,255,0.9)' : '#1e293b';
  const accentColor = isDark ? '#F59E0B' : '#2563eb';
  const accentBg = isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)';
  const accentText = isDark ? '#1C1814' : '#ffffff';
  const cardTitleText = isDark ? 'rgba(255,255,255,0.9)' : '#0f172a';
  const cardBodyText = isDark ? 'rgba(255,255,255,0.65)' : '#334155';
  const cardMutedText = isDark ? 'rgba(255,255,255,0.38)' : '#64748b';
  const cardMetaText = isDark ? 'rgba(255,255,255,0.3)' : '#475569';
  const cardDivider = isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0';
  const sectionBlueTitle = isDark ? 'rgba(147,197,253,0.9)' : '#1d4ed8';
  const sectionBlueMeta = isDark ? 'rgba(147,197,253,0.6)' : '#2563eb';
  const sectionPurpleTitle = isDark ? 'rgba(196,181,253,0.9)' : '#6d28d9';
  const sectionPurpleMeta = isDark ? 'rgba(196,181,253,0.6)' : '#7c3aed';
  const sectionGreenTitle = isDark ? 'rgba(110,231,183,0.9)' : '#047857';
  const sectionGreenMeta = isDark ? 'rgba(110,231,183,0.6)' : '#059669';
  const selectBtnUnselected = isDark
    ? { background: '#0F172A', border: '1px solid rgba(96,165,250,0.65)', color: '#BFDBFE' }
    : { background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155' };
  const selectBtnSelected = isDark
    ? { background: '#3B82F6', border: '1px solid #3B82F6', color: '#ffffff', boxShadow: '0 0 0 1px rgba(147,197,253,0.55)' }
    : { background: '#3B82F6', border: '1px solid #3B82F6', color: '#ffffff', boxShadow: '0 0 0 1px rgba(59,130,246,0.22)' };
  const selectBtnHoverBorder = isDark ? '#3B82F6' : '#93c5fd';
  const bottomBarContainer = isDark
    ? {
      background: 'linear-gradient(90deg, rgba(13,16,25,0.95), rgba(15,23,42,0.95), rgba(13,16,25,0.95))',
      border: '1px solid rgba(245,158,11,0.45)',
      boxShadow: '0 10px 26px rgba(245,158,11,0.2)',
      color: '#FAFAF9'
    }
    : {
      background: 'linear-gradient(90deg, rgba(255,255,255,0.97), rgba(248,250,252,0.97), rgba(255,255,255,0.97))',
      border: '1px solid #cbd5e1',
      boxShadow: '0 10px 26px rgba(15,23,42,0.1)',
      color: '#0f172a'
    };
  const bottomBarCountPill = isDark
    ? { background: 'rgba(245,158,11,0.18)', color: '#FCD34D', boxShadow: '0 8px 20px rgba(245,158,11,0.25)' }
    : { background: '#DBEAFE', color: '#1D4ED8', boxShadow: '0 6px 14px rgba(59,130,246,0.18)' };
  const bottomBarSubtext = isDark ? '#FCD34D' : '#475569';
  const btnFiltAct = isDark
    ? { background: 'rgba(245,158,11,0.2)', color: accentColor, border: '1px solid rgba(245,158,11,0.4)' }
    : { background: 'rgba(37,99,235,0.1)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.3)' };
  const btnFiltInact = isDark
    ? { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.1)' }
    : { background: 'rgba(59,130,246,0.05)', color: '#64748b', border: '1px solid #e2e8f0' };
  const profileMenuBg = isDark ? '#0d1019' : '#ffffff';
  const profileMenuBorder = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0';

  if (authLoading) {
    return (
      <div className={`${appCls} compact-ui flex items-center justify-center`} style={{ minHeight: '100vh' }}>
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: accentColor }} />
      </div>
    );
  }

  return (
    <div className={`${appCls} compact-ui`} style={{ paddingTop: 56 }}>
      {tourOpen && (
        <>
          <div className="fixed inset-0 z-50 pointer-events-none" style={{ background: 'rgba(0,0,0,0.6)' }} />
          <div
            className="fixed z-[80] rounded-2xl p-5 shadow-xl"
            style={{
              background: isDark ? '#0d1019' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              top: `${tourPanelPos.top}px`,
              left: `${tourPanelPos.left}px`,
              width: `${tourPanelPos.width}px`,
              boxShadow: isDark ? 'none' : '0 12px 32px rgba(59,130,246,0.15)'
            }}
          >
            <p className="text-xs uppercase tracking-wide" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b', letterSpacing: '0.12em' }}>
              Step {tourStep + 1} of {DASHBOARD_TOUR_STEPS.length}
            </p>
            <h3 className="mt-1 text-lg font-bold" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b', fontFamily: "'Playfair Display', serif" }}>{DASHBOARD_TOUR_STEPS[tourStep].title}</h3>
            {DASHBOARD_TOUR_STEPS[tourStep]?.key === 'filters-group' ? (
              <div className="mt-2 space-y-2 text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : '#475569' }}>
                <p><span className="font-semibold" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b' }}>Search name:</span> Search any company to see if it appears in the transition list.</p>
                <p><span className="font-semibold" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b' }}>Search role:</span> Filter by role to see which companies hired for this specific position.</p>
                <p><span className="font-semibold" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b' }}>Year:</span> Select a year to view transitions that happened then. Tip — pick a year close to when you left this company.</p>
                <p><span className="font-semibold" style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b' }}>Transition:</span> See where people go after their 1st, 2nd, or 3rd job after leaving this company.</p>
              </div>
            ) : (
              <p className="mt-1 text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.55)' : '#475569' }}>{renderHighlightedTourText(getTourDescription())}</p>
            )}
            <div className="mt-5 flex items-center justify-between">
              <button
                type="button"
                onClick={closeTour}
                className="text-sm transition-colors"
                style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b' }}
                onMouseEnter={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.65)' : '#1e293b'}
                onMouseLeave={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.35)' : '#64748b'}
              >
                Skip tour
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={prevTourStep}
                  disabled={tourStep === 0}
                  className="h-9 px-4 rounded-full text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`, color: isDark ? 'rgba(255,255,255,0.6)' : '#64748b' }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={tourStep === DASHBOARD_TOUR_STEPS.length - 1 ? closeTour : nextTourStep}
                  className="h-9 px-6 rounded-full text-sm font-bold transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/10"
                  style={{ background: accentBg, color: isDark ? '#1a1612' : '#ffffff' }}
                >
                  {tourStep === DASHBOARD_TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Header */}
      <header className="z-50" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 56, background: bgPage, borderBottom: `1px solid ${borderSub}`, backdropFilter: 'blur(12px)' }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center justify-between gap-3" style={{ height: 56 }}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all"
              style={{ color: txtMuted }}
              onMouseEnter={e => { e.currentTarget.style.color = txtBody; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = txtMuted; e.currentTarget.style.background = 'transparent'; }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="h-6 w-px" style={{ background: borderSub }} />
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <div className="w-8 h-8 flex items-center justify-center bg-white rounded-md p-1">
                <img src="/assets/logo.png" alt="fairhire logo" className="w-full h-full object-contain" />
              </div>
              <span
                className="text-xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif", color: txtBody }}
              >
                fairhire
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Theme toggle */}
            <ThemeToggle />
            {!tourOpen && (
              <button
                type="button"
                onClick={startTour}
                title="Start product tour"
                aria-label="Start product tour"
                className="group h-10 w-10 hover:w-32 hover:-translate-x-1 rounded-full transition-all duration-300 overflow-hidden flex items-center"
                style={{ border: `1px solid ${accentColor}4d`, background: `${accentColor}14` }}
              >
                <span className="w-10 h-10 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5" style={{ color: accentColor }} />
                </span>
                <span className="text-sm font-semibold whitespace-nowrap max-w-0 opacity-0 group-hover:max-w-[88px] group-hover:opacity-100 transition-all duration-300" style={{ color: accentColor }}>
                  start tour
                </span>
              </button>
            )}
            <div className="relative">
              {user ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                    style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.08)' }}
                  >
                    <UserIcon className="w-5 h-5" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#3b82f6' }} />
                  </button>

                  <AnimatePresence>
                    {isProfileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute right-0 top-full mt-2 w-52 rounded-xl shadow-xl p-2 z-40"
                        style={{ background: profileMenuBg, border: `1px solid ${profileMenuBorder}`, boxShadow: isDark ? 'none' : '0 8px 32px rgba(59,130,246,0.12)' }}
                      >
                        <div className="px-3 py-2 mb-1" style={{ borderBottom: `1px solid ${profileMenuBorder}` }}>
                          <p className="text-xs" style={{ color: txtDim }}>Signed in as</p>
                          <p className="text-sm font-semibold truncate" style={{ color: txtBody }}>{user.name}</p>
                        </div>
                        <button
                          onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all"
                          style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#475569' }}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <UserIcon className="w-4 h-4" />
                          Profile
                        </button>
                        <button
                          onClick={() => { logout(); setIsProfileMenuOpen(false); navigate('/'); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all"
                          style={{ color: '#F87171' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  onClick={() => openLogin()}
                  className="px-6 py-2 rounded-full text-sm font-semibold transition-all"
                  style={{ background: `${accentColor}26`, color: accentColor, border: `1px solid ${accentColor}4d` }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Company + tenure summary */}
      <div style={{ background: bgSub, borderBottom: `1px solid ${borderSub}` }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4">
          <div
            className={`rounded-2xl px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${isTourTarget('context') ? `relative z-[70] ring-2 ${isDark ? 'ring-amber-400/40' : 'ring-blue-500/40'}` : ''}`}
            style={{ background: bgCard, border: `1px solid ${borderCard}` }}
            data-tour="context"
          >
            <div className="space-y-2 text-sm" style={{ color: txtMuted }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: txtMuted }}>
                Career transitions context
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span>Career transitions of</span>
                <div className="relative w-full sm:w-auto sm:min-w-[200px]">
                  <input
                    type="text"
                    value={topCompanyName}
                    onChange={(e) => {
                      setHasTouchedCompanyInput(true);
                      setSelectedOrg(null);
                      setTopCompanyName(e.target.value);
                    }}
                    onFocus={() => {
                      setHasTouchedCompanyInput(true);
                      if (orgSuggestions.length > 0) setShowOrgSuggestions(true);
                    }}
                    onBlur={() => setTimeout(() => setShowOrgSuggestions(false), 180)}
                    placeholder="Search company..."
                    className="h-9 w-full px-3 rounded-full text-sm focus:outline-none focus:ring-0"
                    style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                  />
                  {orgSuggestionsLoading && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 text-[#78716C] animate-spin" />
                    </span>
                  )}
                  {showOrgSuggestions && orgSuggestions.length > 0 && (
                    <ul className="absolute left-0 right-0 top-full mt-1 py-1 rounded-xl shadow-lg z-50 max-h-56 overflow-auto"
                      style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
                      {orgSuggestions.map((org) => (
                        <li key={org.id}>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedOrg(org);
                              setTopCompanyName(org.name);
                              setShowOrgSuggestions(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm flex justify-between items-center transition-colors"
                            style={{ color: inputText }}
                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(59,130,246,0.06)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span>{org.name}</span>
                            {org.alumni_count != null && (
                              <span className="text-xs" style={{ color: txtMuted }}>{org.alumni_count} alumni</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <span>alumni who left between</span>
                <select
                  value={topStartYear || ''}
                  onChange={(e) => setTopStartYear(e.target.value)}
                  className="h-9 w-full sm:w-auto px-3 rounded-full text-sm focus:outline-none focus:ring-0"
                  style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                >
                  <option value="">Start year</option>
                  {years.map((year) => (
                    <option key={`top-start-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <span>and</span>
                <select
                  value={topEndYear || ''}
                  onChange={(e) => setTopEndYear(e.target.value)}
                  className="h-9 w-full sm:w-auto px-3 rounded-full text-sm focus:outline-none focus:ring-0"
                  style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                >
                  <option value="">End year</option>
                  {years.map((year) => (
                    <option key={`top-end-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-start gap-4 w-full md:w-auto">
              <div className="text-left sm:text-right">
                <p className="text-xs" style={{ color: txtDim }}>Alumni in this view</p>
                <p
                  className="text-2xl font-bold"
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: accentColor }}
                >
                  {totalAlumni}
                </p>
              </div>
              <button
                type="button"
                onClick={handleUpdateTopContext}
                disabled={updatingOrgContext}
                className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                style={{ background: accentBg, color: accentText }}
              >
                {updatingOrgContext ? 'Updating...' : 'Update view'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-[72px] z-30">
        {/* Filters */}
        <div style={{ background: bgFilter, borderBottom: `1px solid ${borderSub}`, backdropFilter: 'blur(10px)' }}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div
                className={`flex flex-wrap gap-3 items-center ${isTourTarget('filters-group') ? `relative z-[60] ring-4 ${isDark ? 'ring-[#F59E0B]/45' : 'ring-[#2563eb]/45'} rounded-xl p-2 -m-2` : ''}`}
                data-tour="filters-group"
              >
                {/* Search */}
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: txtMuted }} />
                  <input
                    type="text"
                    placeholder="Search company..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="w-full sm:w-48 pl-9 pr-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                    data-testid="search-name-input"
                  />
                </div>

                {/* Role Search */}
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: txtMuted }} />
                  <input
                    type="text"
                    placeholder="Role (e.g. Product)..."
                    value={contextRole}
                    onChange={(e) => setContextRole(e.target.value)}
                    onBlur={applyRoleContext}
                    onKeyDown={(e) => e.key === 'Enter' && applyRoleContext()}
                    className="w-full sm:w-48 pl-9 pr-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                    data-testid="role-search-input"
                  />
                </div>

                {/* Year Filter */}
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedYear(v);
                    if (v !== 'all') incrementActivationCounter('year_filter_applied');
                  }}
                  className="w-full sm:w-auto px-3 py-2 rounded-lg text-sm focus:outline-none cursor-pointer"
                  style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                  data-testid="year-filter"
                >
                  <option value="all">All Years</option>
                  {filterYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>

                {/* Transition Filter */}
                <select
                  value={selectedTransition}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTransition(v);
                    if (v !== 'all') incrementActivationCounter('transition_filter_applied');
                  }}
                  className="w-full sm:w-auto px-3 py-2 rounded-lg text-sm focus:outline-none cursor-pointer"
                  style={{ border: `1px solid ${inputBorder}`, background: inputBg, color: inputText }}
                  data-testid="transition-filter"
                >
                  <option value="all">All Transitions</option>
                  <option value="1">1st Transition</option>
                  <option value="2">2nd Transition</option>
                  <option value="3+">3+ Transitions</option>
                </select>
              </div>

              {/* Advanced Connections Filter */}
              <button
                type="button"
                onClick={() => {
                  setIsFilterOpen(true);
                  incrementActivationCounter('connections_filter_opened');
                }}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isTourTarget('connections-filter') ? `relative z-[70] ring-2 ${isDark ? 'ring-amber-400/40' : 'ring-blue-500/40'}` : ''}`}
                style={isConnectionFilterActive ? btnFiltAct : btnFiltInact}
                data-tour="connections-filter"
              >
                <SlidersHorizontal className="w-4 h-4" style={{ color: isConnectionFilterActive ? (isDark ? 'rgba(255,255,255,0.7)' : '#2563eb') : txtMuted }} />
                Filters
                {isConnectionFilterActive && appliedFiltersCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full text-xs font-bold w-5 h-5 bg-[#3B82F6] text-white">
                    {appliedFiltersCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  incrementActivationCounter('clear_filters_clicks');
                  clearAllFilters();
                  setShowNoMatchHint(false);
                  setShowContextMismatchHint(false);
                }}
                className="text-sm hover:underline sm:ml-auto" style={{ color: accentColor }}
                data-testid="clear-all-button"
              >
                Clear All
              </button>
              <span className="text-sm" style={{ color: txtMuted }}>
                {isConnectionFilterActive
                  ? `${matchedCompaniesCount}/${totalVisibleCompaniesCount} companies matched`
                  : `Showing ${totalVisibleCompaniesCount} companies`}
              </span>
            </div>
            {showNoMatchHint && (
              <div className="mt-2">
                <p className="inline-flex items-center rounded-full border border-[#FDE68A] bg-[#FEFCE8] px-3 py-1 text-xs text-[#92400E]">
                  Nothing matched your current filters. Try applying new filters ...
                </p>
              </div>
            )}
            {showContextMismatchHint && (
              <div className="mt-2">
                <p className="inline-flex items-center rounded-full border border-[#FDBA74] bg-[#FFF7ED] px-3 py-1 text-xs text-[#9A3412]">
                  No exact match for current role/year/transition filters. Showing available companies instead.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`${isTourTarget('tabs') ? 'relative z-[60]' : ''}`}
          style={{ background: bgFilter, borderBottom: `1px solid ${borderSub}` }}
          data-tour="tabs"
        >
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
            <div className="flex gap-6 overflow-x-auto whitespace-nowrap scrollbar-thin py-1">
              <button
                onClick={() => {
                  if (activeTab !== 'company-pathways') incrementActivationCounter('tabs_switched');
                  setActiveTab('company-pathways');
                }}
                className={`${tabCls} py-4 px-2 text-sm font-medium ${activeTab === 'company-pathways' ? 'active' : ''}`}
                data-testid="company-pathways-tab"
              >
                Company Pathways
              </button>
              <button
                onClick={() => {
                  if (activeTab !== 'career-transitions') incrementActivationCounter('tabs_switched');
                  setActiveTab('career-transitions');
                }}
                className={`${tabCls} py-4 px-2 text-sm font-medium ${activeTab === 'career-transitions' ? 'active' : ''}`}
                data-testid="career-transitions-tab"
              >
                Career Transitions
              </button>
              <button
                onClick={() => {
                  if (activeTab !== 'alumni') incrementActivationCounter('tabs_switched');
                  setActiveTab('alumni');
                }}
                className={`${tabCls} py-4 px-2 text-sm font-medium ${activeTab === 'alumni' ? 'active' : ''}`}
                data-testid="alumni-tab"
              >
                Alumni ({totalAlumni})
              </button>
              <button
                onClick={() => {
                  if (activeTab !== 'employees') incrementActivationCounter('tabs_switched');
                  setActiveTab('employees');
                }}
                className={`${tabCls} py-4 px-2 text-sm font-medium ${activeTab === 'employees' ? 'active' : ''}`}
                data-testid="employees-tab"
              >
                Employees ({employeesTotal || '—'})
              </button>
              <button
                onClick={() => {
                  if (activeTab !== 'statistics') incrementActivationCounter('tabs_switched');
                  setActiveTab('statistics');
                }}
                className={`${tabCls} py-4 px-2 text-sm font-medium ${activeTab === 'statistics' ? 'active' : ''}`}
                data-testid="statistics-tab"
              >
                Statistics
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Connections filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-0"
            style={{ background: 'rgba(5,4,3,0.75)', backdropFilter: 'blur(6px)' }}
          >
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="max-w-4xl w-full mx-0 sm:mx-4 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
              style={{ background: '#0d1019', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="flex items-start justify-between px-4 sm:px-6 pt-5 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: accentColor, letterSpacing: '0.12em' }}>
                    Transitions from {formatSearchName(userSearchValue)}
                  </p>
                  <p className="mt-1 text-sm max-w-xl" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    We'll highlight companies where people like you are working so you can get warm intros.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="p-1.5 rounded-full transition-all"
                  style={{ color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)' }}
                  aria-label="Close filters"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative px-4 sm:px-6 py-5">
                {!profileLoaded ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
                    <p className="text-sm" style={{ color: txtDim }}>Mapping the alumni network...</p>
                  </div>
                ) : isProfileEmpty && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-2xl" style={{ background: 'rgba(28,24,20,0.88)', backdropFilter: 'blur(6px)' }}>
                    <div className="max-w-md mx-4 text-center px-6 py-8">
                      <p className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        Add your profile to use advanced filters.
                      </p>
                      <p className="mt-3 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        Add your work history and education so we can show which companies have people with similar backgrounds.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setIsFilterOpen(false);
                          navigate('/profile');
                        }}
                        className="mt-5 inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
                        style={{ background: accentBg, color: isDark ? '#1a1612' : '#ffffff' }}
                      >
                        Go to Profile
                      </button>
                    </div>
                  </div>
                )}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isProfileEmpty ? 'select-none pointer-events-none blur-md opacity-50' : ''}`}>
                  {/* Past Company Connections */}
                  <div className="space-y-8">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#34D399' }} />
                      <h3 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>
                        Work history
                      </h3>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Find ex-colleagues who are working at these companies.
                    </p>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Past company (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const allCompaniesSelected = (selectedPastCompanies ?? []).includes(ALL_PROFILE_COMPANIES_TOKEN);
                            return (
                              <button
                                type="button"
                                onClick={() => setSelectedPastCompanies((prev) => {
                                  const p = prev ?? [];
                                  return p.includes(ALL_PROFILE_COMPANIES_TOKEN) ? [] : [ALL_PROFILE_COMPANIES_TOKEN];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={allCompaniesSelected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                All
                              </button>
                            );
                          })()}
                          {(profileCompanies ?? []).map((company) => {
                            const selected = (selectedPastCompanies ?? []).includes(company);
                            return (
                              <button
                                key={company}
                                type="button"
                                onClick={() => setSelectedPastCompanies(prev => {
                                  const p = (prev ?? []).filter((c) => c !== ALL_PROFILE_COMPANIES_TOKEN);
                                  return p.includes(company) ? p.filter((c) => c !== company) : [...p, company];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {company}
                              </button>
                            );
                          })}
                          {!isProfileEmpty && (profileCompanies ?? []).length === 0 && (
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Add companies in your profile</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Job title (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const anyRoleSelected = (selectedPastRoles ?? []).includes(ANY_ROLE_TOKEN);
                            return (
                              <button
                                type="button"
                                onClick={() => setSelectedPastRoles((prev) => {
                                  const p = prev ?? [];
                                  return p.includes(ANY_ROLE_TOKEN) ? [] : [ANY_ROLE_TOKEN];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={anyRoleSelected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                Any Role
                              </button>
                            );
                          })()}
                          {(profileRoles ?? []).map((role) => {
                            const selected = (selectedPastRoles ?? []).includes(role);
                            return (
                              <button
                                key={role}
                                type="button"
                                onClick={() => setSelectedPastRoles(prev => {
                                  const p = (prev ?? []).filter((r) => r !== ANY_ROLE_TOKEN);
                                  return p.includes(role) ? p.filter((r) => r !== role) : [...p, role];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {role}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Tenure (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { id: 'with-me', label: 'Worked with me', title: 'Any person with matching or overlapping tenure' },
                            { id: 'near-me', label: 'Near my time', title: 'People whose stint is within 2 years of your timeframe' },
                            { id: 'any-time', label: 'Anytime', title: 'No tenure restriction' },
                          ].map(({ id, label, title }) => {
                            const selected = (selectedTenureOptions ?? []).includes(id);
                            return (
                              <button
                                key={id}
                                type="button"
                                title={title}
                                onClick={() => setSelectedTenureOptions(prev => {
                                  const p = prev ?? [];
                                  if (id === 'any-time') {
                                    return p.includes('any-time') ? p.filter((t) => t !== 'any-time') : ['any-time'];
                                  }
                                  const withoutAny = p.filter((t) => t !== 'any-time');
                                  return withoutAny.includes(id)
                                    ? withoutAny.filter((t) => t !== id)
                                    : [...withoutAny, id];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                          Worked with me: overlapping tenure. Near my time: within 2 years of your stint window. Anytime: no restriction.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Education */}
                  <div className="space-y-8">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#F87171' }} />
                      <h3 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>
                        Education
                      </h3>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Find batchmates and alumni at these companies.
                    </p>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>College / university (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const selectedAllColleges = (selectedColleges ?? []).includes(ALL_PROFILE_COLLEGES_TOKEN);
                            return (
                              <button
                                type="button"
                                onClick={() => setSelectedColleges((prev) => {
                                  const p = prev ?? [];
                                  return p.includes(ALL_PROFILE_COLLEGES_TOKEN) ? [] : [ALL_PROFILE_COLLEGES_TOKEN];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selectedAllColleges
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                All
                              </button>
                            );
                          })()}
                          {(profileColleges ?? []).map((college) => {
                            const selected = (selectedColleges ?? []).includes(college);
                            const label = (college && college.length > 22) ? `${String(college).slice(0, 22)}…` : (college ?? '');
                            return (
                              <button
                                key={college}
                                type="button"
                                title={college}
                                onClick={() => setSelectedColleges(prev => {
                                  const p = (prev ?? []).filter((c) => c !== ALL_PROFILE_COLLEGES_TOKEN);
                                  return p.includes(college) ? p.filter((c) => c !== college) : [...p, college];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Department (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const anyDepartmentSelected = (selectedDepartments ?? []).includes(ANY_DEPARTMENT_TOKEN);
                            return (
                              <button
                                type="button"
                                onClick={() => setSelectedDepartments((prev) => {
                                  const p = prev ?? [];
                                  return p.includes(ANY_DEPARTMENT_TOKEN) ? [] : [ANY_DEPARTMENT_TOKEN];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={anyDepartmentSelected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                Any Department
                              </button>
                            );
                          })()}
                          {(profileDepartments ?? []).map((dept) => {
                            const selected = (selectedDepartments ?? []).includes(dept);
                            const label = (dept && dept.length > 22) ? `${String(dept).slice(0, 22)}…` : (dept ?? '');
                            return (
                              <button
                                key={dept}
                                type="button"
                                title={dept}
                                onClick={() => setSelectedDepartments(prev => {
                                  const p = (prev ?? []).filter((d) => d !== ANY_DEPARTMENT_TOKEN);
                                  return p.includes(dept) ? p.filter((d) => d !== dept) : [...p, dept];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Batch (multi-select)</p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { id: 'exact', label: 'Batchmates' },
                            { id: 'close', label: 'Close batch' },
                            { id: 'any', label: 'Any batch' },
                          ].map(({ id, label }) => {
                            const selected = (selectedBatchOptions ?? []).includes(id);
                            return (
                              <button
                                key={id}
                                type="button"
                                onClick={() => setSelectedBatchOptions(prev => {
                                  const p = prev ?? [];
                                  if (id === 'any') {
                                    return p.includes('any') ? p.filter((b) => b !== 'any') : ['any'];
                                  }
                                  const withoutAny = p.filter((b) => b !== 'any');
                                  return withoutAny.includes(id)
                                    ? withoutAny.filter((b) => b !== id)
                                    : [...withoutAny, id];
                                })}
                                className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                                style={selected
                                  ? { background: accentBg, color: isDark ? '#1a1612' : '#ffffff', border: '1px solid transparent' }
                                  : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)', color: isDark ? 'rgba(255,255,255,0.65)' : '#64748b', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.1)' }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                          Batchmates: same start and end dates. Close batch: ±4 years from start year. Any batch: no restriction.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
                <p className="text-xs max-w-xl" style={{ color: 'rgba(182, 179, 179, 0.77)' }}>
                  Highlights companies where matches exist.
                </p>
                {isProfileEmpty ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsFilterOpen(false);
                      navigate('/profile');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs font-semibold transition-all"
                    style={{ background: accentBg, color: isDark ? '#1a1612' : '#ffffff' }}
                  >
                    Go to Profile
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPastCompanies([]);
                        setSelectedPastRoles([]);
                        setSelectedTenureOptions([]);
                        setSelectedColleges([]);
                        setSelectedDepartments([]);
                        setSelectedBatchOptions([]);
                        setAppliedConnectionFilters(null);
                        setShowNoMatchHint(false);
                        setShowContextMismatchHint(false);
                        lastFetchKeyRef.current = null;
                        setIsFilterOpen(false);
                      }}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs font-semibold transition-all"
                      style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.05)' }}
                    >
                      Clear filters
                    </button>
                    <button
                      type="button"
                      onClick={applyFiltersAndClose}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs font-semibold transition-all"
                      style={{ background: accentBg, color: isDark ? '#1a1612' : '#ffffff' }}
                    >
                      Apply filters
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className={`max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-8 ${selectedCount > 0 ? 'pb-36 sm:pb-32' : ''}`}>
        {activeTab === 'company-pathways' && (
          <div>
            {/* Section Header */}
            {(showFirstSection && (loading || error || (filteredCompaniesFirst ?? []).length > 0)) && (
              <div ref={firstTransitionsRef} className="dark-section-header-blue flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-6 px-4 sm:px-6 py-3.5 rounded-lg">
                <h2 className="text-base font-semibold" style={{ color: sectionBlueTitle }}>
                  1st Transitions
                </h2>
                <span className="text-sm" style={{ color: sectionBlueMeta }}>
                  {(filteredCompaniesFirst ?? []).length} companies
                </span>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-24" data-testid="loading-transitions">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: accentColor }} />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg mb-6" data-testid="transitions-error">
                {error}
              </div>
            )}

            {!loading && !error && (
              <>
                {isNoPathwaysData ? (
                  <div className="space-y-8">
                    {/* ── NO DATA YET Banner ── */}
                    {(() => {
                      const notFoundName = searchParams.orgNotFound ? (searchParams.companyName || '') : '';
                      const displayName = notFoundName || searchParams.companyName || '';
                      const isNotFound = !!searchParams.orgNotFound;
                      const isLowDestinationCount = hasInsufficientDestinationCompanies;
                      const showBanner = isNotFound || isLowDestinationCount || (displayName && !hasCompanyFiltersActive);

                      if (!showBanner) {
                        return <p className="text-sm text-white/65 bg-[#0d1019] border border-white/10 rounded-xl px-6 py-5">{companyNoMatchMessage}</p>;
                      }

                      const isNotified = notifiedCompanies.has((displayName || '').toLowerCase());
                      return (
                        <div className="bg-[#FFF7E8] border border-[#FDE68A] rounded-xl px-4 sm:px-6 py-5 mt-7">
                          {/* Row 1: badge + message */}
                          <div className="flex flex-wrap items-center gap-3 mb-4">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-semibold shrink-0">
                              <span className="w-1.5 h-1.5 rounded-full bg-[${accentColor}] inline-block" />
                              Insights coming soon
                            </span>

                            {/* Bigger primary line */}
                            <p className="text-sm text-[#4B5563]">
                              <span className="text-base font-semibold text-[#111827]">
                                {isLowDestinationCount ? (
                                  <>
                                    We found only <span className="text-[#B45309] font-bold">{destinationCompaniesCount || 0}</span> destination companies for{" "}
                                    <span className="text-[#B45309] font-bold">{displayName}</span> (even over 15 years).
                                  </>
                                ) : (
                                  <>
                                    Looks like we don’t have transition data for{" "}
                                    <span className="text-[#B45309] font-bold">{displayName}</span> yet.
                                  </>
                                )}
                              </span>{" "}
                              Click Notify Me to get updates, and inviting a few people from this space will help unlock richer insights faster.
                            </p>
                          </div>
                          {/* Row 2: Notify me button */}
                          <button
                            type="button"
                            onClick={async () => {
                              const companyKey = (displayName || '').toLowerCase();
                              if (isNotified || notifyingCompanies.has(companyKey)) return;
                              if (!user) { openLogin(); return; }

                              setNotifyingCompanies(prev => new Set([...prev, companyKey]));
                              try {
                                incrementActivationCounter('notify_me_clicks', false);
                                trackCoreFeatureUsed('notify_me_clicked', { company_name: displayName });
                                await addNotifyRequest(displayName);
                                setNotifiedCompanies(prev => new Set([...prev, companyKey]));
                                toast.success(`We'll notify you when ${displayName} data is available!`);
                              } catch (err) {
                                toast.error(err.message || 'Failed to save notification request');
                              } finally {
                                setNotifyingCompanies(prev => {
                                  const next = new Set(prev);
                                  next.delete(companyKey);
                                  return next;
                                });
                              }
                            }}
                            disabled={notifyingCompanies.has((displayName || '').toLowerCase())}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${isNotified
                              ? 'bg-[#000000] text-[#ffffff] border border-[#86EFAC] cursor-default'
                              : 'bg-black text-[#ffffff] border border-[#D1D5DB] hover:bg-[#000000] shadow-sm'
                              } ${notifyingCompanies.has((displayName || '').toLowerCase()) ? 'opacity-70 cursor-wait' : ''}`}
                          >
                            {isNotified ? (
                              <><Check size={16} className="text-[#86EFAC]" /> Notification on</>
                            ) : notifyingCompanies.has((displayName || '').toLowerCase()) ? (
                              <><Loader2 size={16} className="animate-spin" /> Processing...</>
                            ) : (
                              <><span className="text-base">🔔</span> Notify me</>
                            )}
                          </button>
                        </div>
                      );
                    })()}

                    {shouldShowNoDataRecoverySections && (
                      <>
                        {/* ── Explore Similar Companies (final section) ── */}
                        <div className="mt-10 bg-[#FFF7E8] border border-[#D1D5DB] rounded-xl px-4 sm:px-6 py-5">
                          <div className="flex items-end justify-between gap-4 mb-3">
                            <div>
                              <h3 className="text-base font-semibold text-[#111827]">Explore Similar Companies</h3>
                              <p className="text-xs text-[#4B5563]">
                                Based on your search/profile overlap. Showing up to 10 companies with strong alumni data.
                              </p>
                            </div>
                          </div>

                          {similarCompaniesLoading ? (
                            <div className="flex items-center gap-2 text-sm text-[#4B5563] py-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Finding similar companies...</span>
                            </div>
                          ) : (similarCompanies ?? []).length === 0 ? (
                            <p className="text-sm text-[#4B5563] py-2">No similar companies available right now.</p>
                          ) : (
                            <div className="flex gap-3 overflow-x-auto pb-1">
                              {similarCompanies.slice(0, 10).map((c) => (
                                <div
                                  key={`${c.organization_id}-${c.organization}`}
                                  onClick={() => handleSimilarCompanyClick(c)}
                                  className="shrink-0 min-w-[220px] rounded-lg border border-[#D1D5DB] bg-[#F9FAFB] px-4 py-3 cursor-pointer hover:border-[#3B82F6] hover:bg-[#EFF6FF] transition-colors"
                                >
                                  <p className="text-sm font-semibold text-[#111827] truncate">{c.organization || 'Unknown Company'}</p>
                                  <p className="text-xs text-[#4B5563] mt-1">{Number(c.alumni_count || 0)} alumni</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* ── Dark Invite Section ── */}
                        {(() => {
                          const referralId = user?.id ? `u${user.id}` : 'join';
                          const referralLink = `https://fairhire.io/ref/${referralId}`;
                          return (
                            <div
                              className="rounded-2xl overflow-hidden mt-10 lg:mt-8 border border-[#2F3A52] ring-1 ring-white/10 shadow-[0_22px_44px_rgba(2,6,23,0.55)]"
                              style={{
                                backgroundColor: '#0d1019',
                                backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), radial-gradient(circle at 82% 18%, rgba(212,168,75,0.22), transparent 42%), linear-gradient(135deg, #0d1019 0%, #0d1019 55%, #0d1019 100%)',
                                backgroundSize: '18px 18px, 100% 100%, 100% 100%',
                                backgroundPosition: '0 0, 0 0, 0 0'
                              }}
                            >
                              <div className="flex flex-col lg:flex-row gap-8 px-4 sm:px-8 py-6 sm:py-8">
                                {/* Left: copy */}
                                <div className="flex-1 min-w-0 flex flex-col">
                                  <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                                    Your next opportunity is closer than you think,
                                  </h2>
                                  <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif", color: '#D4A84B' }}>
                                    You just can't see it yet.
                                  </h2>

                                  <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6 max-w-lg pt-4">
                                    Your batchmates and ex-colleagues are landing new roles, moving to new companies, and building connections that could matter to you. fairhire makes those moves visible so when the <span className="font-semibold text-[#C8964A]">right opportunity</span> comes up, you're already in the picture.
                                  </p>

                                  {/* Stats */}
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 sm:pt-8">
                                    <div>
                                      <p className="text-2xl font-bold" style={{ color: '#D4A84B', fontFamily: "'JetBrains Mono', monospace" }}>3.2x</p>
                                      <p className="text-xs text-[#7C8798] mt-0.5">more opportunities visible <br />when your network grows</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-bold" style={{ color: '#D4A84B', fontFamily: "'JetBrains Mono', monospace" }}>6+</p>
                                      <p className="text-xs text-[#7C8798] mt-0.5">hidden moves <br />revealed per invite</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-bold" style={{ color: '#D4A84B', fontFamily: "'JetBrains Mono', monospace" }}>339</p>
                                      <p className="text-xs text-[#7C8798] mt-0.5">doors opened through <br />network connections</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Right: share invite */}
                                <div className="w-full lg:w-[26rem] shrink-0">
                                  <div className="rounded-xl border border-[#2A3345] bg-[#171C29]/85 p-4 backdrop-blur-sm">
                                    <p className="text-white font-semibold text-xl mb-0.5">Share invite via</p>
                                    <p className="text-[#97A0AF] text-xs mb-3">Send your referral link directly where your colleagues already are</p>

                                    <button
                                      type="button"
                                      onClick={() => openReferralShare('whatsapp', referralLink)}
                                      className="w-full mb-2 rounded-xl border border-[#1B6E49] bg-[#123326] hover:bg-[#174432] transition-colors text-left px-3 py-2"
                                    >
                                      <span className="flex items-center gap-3">
                                        <img src="/assets/whatsapp-svgrepo-com.svg" alt="WhatsApp" className="w-7 h-7 bg-white rounded-md p-1 shrink-0" />
                                        <span className="min-w-0">
                                          <span className="block text-white font-semibold text-base leading-tight">WhatsApp</span>
                                          <span className="block text-[#95B7A8] text-xs leading-tight">Send via chat or group</span>
                                        </span>
                                      </span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => openReferralShare('gmail', referralLink)}
                                      className="w-full mb-2 rounded-xl border border-[#7B2D2D] bg-[#321A1A] hover:bg-[#412020] transition-colors text-left px-3 py-2"
                                    >
                                      <span className="flex items-center gap-3">
                                        <img src="/assets/gmail-old-svgrepo-com.svg" alt="Gmail" className="w-7 h-7 bg-white rounded-md p-1 shrink-0" />
                                        <span className="min-w-0">
                                          <span className="block text-white font-semibold text-base leading-tight">Gmail</span>
                                          <span className="block text-[#C0A5A5] text-xs leading-tight">Send via email</span>
                                        </span>
                                      </span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => openReferralShare('linkedin', referralLink)}
                                      className="w-full rounded-xl border border-[#1E4D8D] bg-[#12283F] hover:bg-[#173252] transition-colors text-left px-3 py-2"
                                    >
                                      <span className="flex items-center gap-3">
                                        <img src="/assets/linkedin-svgrepo-com.svg" alt="LinkedIn" className="w-7 h-7 bg-white rounded-md p-1 shrink-0" />
                                        <span className="min-w-0">
                                          <span className="block text-white font-semibold text-base leading-tight">LinkedIn</span>
                                          <span className="block text-[#9FB5CE] text-xs leading-tight">Open chat compose</span>
                                        </span>
                                      </span>
                                    </button>

                                    <div className="flex items-center gap-3 my-3">
                                      <div className="flex-1 h-px bg-[#2A3345]" />
                                      <span className="text-[#6B7280] text-xs">or copy link</span>
                                      <div className="flex-1 h-px bg-[#2A3345]" />
                                    </div>

                                    <div className="flex items-center gap-2 rounded-lg border border-[#323D54] bg-[#0D1422] px-3 py-2.5 mb-3">
                                      <span className="flex-1 text-[#7C8798] text-xs truncate">{referralLink}</span>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            incrementActivationCounter('referral_copy_clicks', false);
                                            trackCoreFeatureUsed('referral_link_copied', { referral_link: referralLink });
                                            await addReferral(referralLink);
                                          } catch {
                                            // Ignore tracking failures for copy action.
                                          }
                                          navigator.clipboard?.writeText(referralLink);
                                          toast.success('Referral link copied!');
                                        }}
                                        className="shrink-0 text-xs font-semibold text-[#D4A84B] hover:text-[#F0C060] transition-colors"
                                      >
                                        Copy link
                                      </button>
                                    </div>

                                    <p className="text-[#7C8798] text-xs leading-relaxed">
                                      Invite few people you trust to unlock deeper insights faster.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}

                  </div>
                ) : (showFirstSection && (filteredCompaniesFirst ?? []).length > 0) ? (
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    {filteredCompaniesFirst.map((company, index) => (
                      <motion.div
                        key={company.name ? `${company.name}-${index}` : index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.4 }}
                        whileHover={isSelectionMode ? undefined : { y: -4 }}
                        className={`${cardCls} relative overflow-hidden rounded-xl p-4 sm:p-6 transition-all ${isSelectionMode ? 'cursor-default' : 'cursor-pointer'} ${(isOrgHighlighted(company.organizationId) || (appliedConnectionFilters && (company.filterMatchCount || 0) > 0)) ? 'blue-highlight' : ''} ${(isTourTarget('company-card') && index === 0) ? 'relative z-[60]' : ''}`}
                        data-tour={index === 0 ? 'company-card' : undefined}
                        data-testid={`company-card-${company.rank}`}
                        onClick={() => {
                          if (isSelectionMode) return;
                          incrementActivationCounter('company_card_clicks', false);
                          incrementActivationCounter('company_cards_opened');
                          trackCoreFeatureUsed('company_card_opened', { hop: 1, company_name: company.name, dest_org_id: company.organizationId });
                          trackCompanyClicked(company.name, {
                            source: 'dashboard',
                            click_target: 'company_card',
                            hop: 1,
                            dest_org_id: company.organizationId,
                            source_org_id: searchParams.orgId,
                            source_company_name: searchParams.companyName,
                            start_year: searchParams.startYear,
                            end_year: searchParams.endYear,
                          });
                          openCompanyDetailsInNewTab(company, 1);
                        }}
                      >
                        <div className={`absolute left-0 top-0 h-full`} style={{ width: isCardSelected(1, company, index) ? 3 : 1, background: isCardSelected(1, company, index) ? '#60A5FA' : 'rgba(255,255,255,0.07)' }} />
                        {/* Rank Badge + Related Badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="inline-flex items-center justify-center text-sm font-bold rounded px-2 py-1" style={{ background: 'rgba(96,165,250,0.15)', color: '#93C5FD' }}>
                            #{company.rank}
                          </div>
                          {company.relatedCount > 0 && (
                            <div className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: 'rgba(52,211,153,0.12)', color: '#6EE7B7' }}>
                              <span>👥</span>
                              <span>{company.relatedCount} connection{company.relatedCount !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCardSelected(1, company, index);
                            }}
                            className="ml-auto h-8 px-2.5 rounded-full inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold transition-colors"
                            style={isCardSelected(1, company, index) ? selectBtnSelected : selectBtnUnselected}
                            onMouseEnter={(e) => {
                              if (!isCardSelected(1, company, index)) e.currentTarget.style.borderColor = selectBtnHoverBorder;
                            }}
                            onMouseLeave={(e) => {
                              if (!isCardSelected(1, company, index)) e.currentTarget.style.borderColor = isDark ? 'rgba(96,165,250,0.65)' : '#cbd5e1';
                            }}
                            aria-label={isCardSelected(1, company, index) ? 'Unselect from Find Jobs list' : 'Select to Find Jobs'}
                            title={isCardSelected(1, company, index) ? 'Selected for Find Jobs' : 'Select to Find Jobs'}
                          >
                            {isCardSelected(1, company, index) && <Check className="w-3.5 h-3.5 text-white" />}
                            <span>{isCardSelected(1, company, index) ? 'Selected' : 'Select for Jobs'}</span>
                          </button>
                        </div>

                        {/* Company Name + Careers */}
                        <div className="flex items-center gap-2 mb-4">
                          <h3
                            className="text-lg font-bold"
                            style={{ fontFamily: "'Inter', sans-serif", color: cardTitleText }}
                          >
                            {company.name}
                          </h3>
                          <a
                            href={`https://google.com/search?q=${encodeURIComponent(company.name)}+careers`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isSelectionMode) e.preventDefault();
                            }}
                            className={`inline-flex items-center group ${isSelectionMode ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <ExternalLink
                              className="w-4 h-4 transition-all duration-200 group-hover:-translate-y-0.5"
                              style={{ color: 'rgba(147,197,253,0.85)' }}
                            />
                          </a>
                        </div>

                        {/* People and Last Hire Row */}
                        <div className="flex flex-col sm:flex-row gap-0 mb-4 pb-4" style={{ borderBottom: `1px solid ${cardDivider}` }}>
                          {/* People Column */}
                          <div className="flex-[1.5] sm:pr-4">
                            <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: 'rgba(96,165,250,0.1)', color: '#93C5FD', border: '1px solid rgba(96,165,250,0.2)' }}>
                              People
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                              <div className={`${isTourTarget('people-first') && index === 0 ? `rounded-lg ring-4 ${isDark ? 'ring-[#F59E0B]/45' : 'ring-[#2563eb]/45'} p-2 -m-2` : ''}`} data-tour={index === 0 ? 'people-first' : undefined}>
                                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>1st Transition</p>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.people}</p>
                              </div>
                              <div className={`${isTourTarget('people-other') && index === 0 ? `rounded-lg ring-4 ${isDark ? 'ring-[#F59E0B]/45' : 'ring-[#2563eb]/45'} p-2 -m-2` : ''}`} data-tour={index === 0 ? 'people-other' : undefined}>
                                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Other</p>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.otherHopsCount}</p>
                              </div>
                            </div>
                          </div>

                          {/* Separator */}
                          <div className="hidden sm:block w-px self-stretch" style={{ background: cardDivider }} />

                          {/* Hiring Column */}
                          <div className="flex-1 pt-4 sm:pt-0 sm:pl-6">
                            <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', color: accentColor, border: isDark ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                              Hiring
                            </div>
                            <div className={`${isTourTarget('recent') && index === 0 ? 'rounded-lg p-2 -m-2' : ''}`} data-tour={index === 0 ? 'recent' : undefined}>
                              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Last Hire</p>
                              <div className="flex items-center gap-1">
                                <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.recent}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Active Years Row */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cardMutedText }}>Active Hiring Years</p>
                          <div className={`flex flex-wrap gap-1.5 ${isTourTarget('years-list') && index === 0 ? `rounded-lg ring-4 ${isDark ? 'ring-[#F59E0B]/45' : 'ring-[#2563eb]/45'} p-2 -m-2` : ''}`} data-tour={index === 0 ? 'years-list' : undefined}>
                            {(company.years || []).slice(0, 6).map((year, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs font-bold rounded border" style={{ background: i === 0 ? (isDark ? 'rgba(245,158,11,0.05)' : 'rgba(59,130,246,0.05)') : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)'), borderColor: i === 0 ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)') : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'), color: i === 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.4)' : '#64748b'), fontFamily: "'JetBrains Mono', monospace" }}>
                                {year}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : null}
              </>
            )}
            {/* 2nd Transitions Section */}
            {(filteredCompaniesSecond ?? []).length > 0 && (
              <>
                <div
                  ref={secondTransitionsRef}
                  className={`dark-section-header-purple flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 ${hasVisibleFirstSection ? 'mt-10' : 'mt-0'} mb-6 px-4 sm:px-6 py-3.5 rounded-lg`}
                >
                  <h2 className="text-base font-semibold" style={{ color: sectionPurpleTitle }}>
                    2nd Transitions
                  </h2>
                  <span className="text-sm" style={{ color: sectionPurpleMeta }}>
                    {(filteredCompaniesSecond ?? []).length} companies
                  </span>
                </div>

                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {filteredCompaniesSecond.map((company, index) => (
                    <motion.div
                      key={company.name ? `${company.name}-2nd-${index}` : `2nd-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                      whileHover={isSelectionMode ? undefined : { y: -4 }}
                      className={`${cardCls} relative overflow-hidden rounded-xl p-4 sm:p-6 transition-all ${isSelectionMode ? 'cursor-default' : 'cursor-pointer'} ${(isOrgHighlighted(company.organizationId) || (appliedConnectionFilters && (company.filterMatchCount || 0) > 0)) ? 'blue-highlight' : ''}`}
                      onClick={() => {
                        if (isSelectionMode) return;
                        incrementActivationCounter('company_card_clicks', false);
                        incrementActivationCounter('company_cards_opened');
                        trackCoreFeatureUsed('company_card_opened', { hop: 2, company_name: company.name, dest_org_id: company.organizationId });
                        trackCompanyClicked(company.name, {
                          source: 'dashboard',
                          click_target: 'company_card',
                          hop: 2,
                          dest_org_id: company.organizationId,
                          source_org_id: searchParams.orgId,
                          source_company_name: searchParams.companyName,
                          start_year: searchParams.startYear,
                          end_year: searchParams.endYear,
                        });
                        openCompanyDetailsInNewTab(company, 2);
                      }}
                    >
                      <div className="absolute left-0 top-0 h-full" style={{ width: isCardSelected(2, company, index) ? 3 : 1, background: isCardSelected(2, company, index) ? '#A78BFA' : 'rgba(255,255,255,0.07)' }} />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="inline-flex items-center justify-center text-sm font-bold rounded px-2 py-1" style={{ background: 'rgba(167,139,250,0.15)', color: '#C4B5FD' }}>
                          #{company.rank}
                        </div>
                        {company.relatedCount > 0 && (
                          <div className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: 'rgba(52,211,153,0.12)', color: '#6EE7B7' }}>
                            <span>👥</span>
                            <span>{company.relatedCount} connection{company.relatedCount !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardSelected(2, company, index);
                          }}
                          className="ml-auto h-8 px-2.5 rounded-full inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold transition-colors"
                          style={isCardSelected(2, company, index) ? selectBtnSelected : selectBtnUnselected}
                          onMouseEnter={(e) => {
                            if (!isCardSelected(2, company, index)) e.currentTarget.style.borderColor = selectBtnHoverBorder;
                          }}
                          onMouseLeave={(e) => {
                            if (!isCardSelected(2, company, index)) e.currentTarget.style.borderColor = isDark ? 'rgba(96,165,250,0.65)' : '#cbd5e1';
                          }}
                          aria-label={isCardSelected(2, company, index) ? 'Unselect from Find Jobs list' : 'Select to Find Jobs list'}
                          title={isCardSelected(2, company, index) ? 'Selected for Find Jobs' : 'Select to Find Jobs'}
                        >
                          {isCardSelected(2, company, index) && <Check className="w-3.5 h-3.5 text-white" />}
                          <span>{isCardSelected(2, company, index) ? 'Selected' : 'Select for Jobs'}</span>
                        </button>
                      </div>

                      <div className="flex items-center gap-2 mb-4">
                        <h3
                          className="text-lg font-bold"
                          style={{ fontFamily: "'Inter', sans-serif", color: cardTitleText }}
                        >
                          {company.name}
                        </h3>
                        <a
                          href={`https://google.com/search?q=${encodeURIComponent(company.name)}+careers`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isSelectionMode) e.preventDefault();
                          }}
                          className={`inline-flex items-center group ${isSelectionMode ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <ExternalLink className="w-4 h-4 transition-all duration-200 group-hover:-translate-y-0.5" style={{ color: 'rgba(196,181,253,0.85)' }} />
                        </a>
                      </div>

                      {/* People and Last Hire Row */}
                      <div className="flex flex-col sm:flex-row gap-0 mb-4 pb-4" style={{ borderBottom: `1px solid ${cardDivider}` }}>
                        {/* People Column */}
                        <div className="flex-[1.5] sm:pr-4">
                          <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: 'rgba(167,139,250,0.1)', color: '#C4B5FD', border: '1px solid rgba(167,139,250,0.2)' }}>
                            People
                          </div>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>2nd Transition</p>
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.people}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Other</p>
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.otherHopsCount}</p>
                            </div>
                          </div>
                        </div>

                        {/* Separator */}
                        <div className="hidden sm:block w-px self-stretch" style={{ background: cardDivider }} />

                        {/* Hiring Column */}
                        <div className="flex-1 pt-4 sm:pt-0 sm:pl-6">
                          <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', color: accentColor, border: isDark ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                            Hiring
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Last Hire</p>
                            <div className="flex items-center gap-1">
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: accentColor }}>{company.recent}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Active Years Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cardMutedText }}>Active Hiring Years</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(company.years || []).slice(0, 6).map((year, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs font-bold rounded border" style={{ background: i === 0 ? (isDark ? 'rgba(245,158,11,0.05)' : 'rgba(59,130,246,0.05)') : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)'), borderColor: i === 0 ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)') : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'), color: i === 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.4)' : '#64748b'), fontFamily: "'JetBrains Mono', monospace" }}>
                              {year}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}

            {/* 3+ Transitions Section */}
            {(filteredCompaniesThird ?? []).length > 0 && (
              <>
                <div
                  ref={thirdTransitionsRef}
                  className={`dark-section-header-green flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 ${(hasVisibleFirstSection || hasVisibleSecondSection) ? 'mt-10' : 'mt-0'} mb-6 px-4 sm:px-6 py-3.5 rounded-lg`}
                >
                  <h2 className="text-base font-semibold" style={{ color: sectionGreenTitle }}>
                    3+ Transitions
                  </h2>
                  <span className="text-sm" style={{ color: sectionGreenMeta }}>
                    {(filteredCompaniesThird ?? []).length} companies
                  </span>
                </div>

                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {filteredCompaniesThird.map((company, index) => (
                    <motion.div
                      key={company.name ? `${company.name}-3rd-${index}` : `3rd-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                      whileHover={isSelectionMode ? undefined : { y: -4 }}
                      className={`${cardCls} relative overflow-hidden rounded-xl p-4 sm:p-6 transition-all ${isSelectionMode ? 'cursor-default' : 'cursor-pointer'} ${(isOrgHighlighted(company.organizationId) || (appliedConnectionFilters && (company.filterMatchCount || 0) > 0)) ? 'blue-highlight' : ''}`}
                      onClick={() => {
                        if (isSelectionMode) return;
                        incrementActivationCounter('company_card_clicks', false);
                        incrementActivationCounter('company_cards_opened');
                        trackCoreFeatureUsed('company_card_opened', { hop: 3, company_name: company.name, dest_org_id: company.organizationId });
                        trackCompanyClicked(company.name, {
                          source: 'dashboard',
                          click_target: 'company_card',
                          hop: 3,
                          dest_org_id: company.organizationId,
                          source_org_id: searchParams.orgId,
                          source_company_name: searchParams.companyName,
                          start_year: searchParams.startYear,
                          end_year: searchParams.endYear,
                        });
                        openCompanyDetailsInNewTab(company, 3);
                      }}
                    >
                      <div className="absolute left-0 top-0 h-full" style={{ width: isCardSelected(3, company, index) ? 3 : 1, background: isCardSelected(3, company, index) ? '#34D399' : 'rgba(255,255,255,0.07)' }} />
                      <div className="flex items-center gap-2 mb-3">
                        <div className="inline-flex items-center justify-center text-sm font-bold rounded px-2 py-1" style={{ background: 'rgba(52,211,153,0.15)', color: '#6EE7B7' }}>
                          #{company.rank}
                        </div>
                        {company.relatedCount > 0 && (
                          <div className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: 'rgba(52,211,153,0.12)', color: '#6EE7B7' }}>
                            <span>👥</span>
                            <span>{company.relatedCount} connection{company.relatedCount !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardSelected(3, company, index);
                          }}
                          className="ml-auto h-8 px-2.5 rounded-full inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold transition-colors"
                          style={isCardSelected(3, company, index) ? selectBtnSelected : selectBtnUnselected}
                          onMouseEnter={(e) => {
                            if (!isCardSelected(3, company, index)) e.currentTarget.style.borderColor = selectBtnHoverBorder;
                          }}
                          onMouseLeave={(e) => {
                            if (!isCardSelected(3, company, index)) e.currentTarget.style.borderColor = isDark ? 'rgba(96,165,250,0.65)' : '#cbd5e1';
                          }}
                          aria-label={isCardSelected(3, company, index) ? 'Unselect from Find Jobs list' : 'Select to Find Jobs list'}
                          title={isCardSelected(3, company, index) ? 'Selected for Find Jobs' : 'Select to Find Jobs'}
                        >
                          {isCardSelected(3, company, index) && <Check className="w-3.5 h-3.5 text-white" />}
                          <span>{isCardSelected(3, company, index) ? 'Selected' : 'Select for Jobs'}</span>
                        </button>
                      </div>

                      <div className="flex items-center gap-2 mb-4">
                        <h3
                          className="text-lg font-bold"
                          style={{ fontFamily: "'Inter', sans-serif", color: cardTitleText }}
                        >
                          {company.name}
                        </h3>
                        <a
                          href={`https://google.com/search?q=${encodeURIComponent(company.name)}+careers`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isSelectionMode) e.preventDefault();
                          }}
                          className={`inline-flex items-center group ${isSelectionMode ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <ExternalLink className="w-4 h-4 transition-all duration-200 group-hover:-translate-y-0.5" style={{ color: 'rgba(110,231,183,0.9)' }} />
                        </a>
                      </div>

                      {/* People and Last Hire Row */}
                      <div className="flex flex-col sm:flex-row gap-0 mb-4 pb-4" style={{ borderBottom: `1px solid ${cardDivider}` }}>
                        {/* People Column */}
                        <div className="flex-[1.5] sm:pr-4">
                          <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: 'rgba(52,211,153,0.1)', color: '#6EE7B7', border: '1px solid rgba(52,211,153,0.2)' }}>
                            People
                          </div>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>3+ Transition</p>
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.people}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Other</p>
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: cardTitleText }}>{company.otherHopsCount}</p>
                            </div>
                          </div>
                        </div>

                        {/* Separator */}
                        <div className="hidden sm:block w-px self-stretch" style={{ background: cardDivider }} />

                        {/* Hiring Column */}
                        <div className="flex-1 pt-4 sm:pt-0 sm:pl-6">
                          <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest mb-3" style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)', color: accentColor, border: isDark ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(59,130,246,0.2)' }}>
                            Hiring
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: cardMetaText }}>Last Hire</p>
                            <div className="flex items-center gap-1">
                              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: accentColor }}>{company.recent}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Active Years Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cardMutedText }}>Active Hiring Years</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(company.years || []).slice(0, 6).map((year, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs font-bold rounded border" style={{ background: i === 0 ? (isDark ? 'rgba(245,158,11,0.05)' : 'rgba(59,130,246,0.05)') : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)'), borderColor: i === 0 ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)') : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'), color: i === 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.4)' : '#64748b'), fontFamily: "'JetBrains Mono', monospace" }}>
                              {year}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </div>
        )}

        {activeTab === 'career-transitions' && (
          <div className="text-center py-16">
            <p style={{ color: txtMuted }}>Career Transitions view coming soon...</p>
          </div>
        )}

        {activeTab === 'alumni' && (
          <div className="space-y-6">
            <div className="dark-section-header-blue flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-6 px-4 sm:px-6 py-3.5 rounded-lg">
              <h2 className="text-base font-semibold" style={{ color: sectionBlueTitle }}>
                Alumni ({(filteredAlumni ?? []).length})
              </h2>
              <span className="text-sm" style={{ color: sectionBlueMeta }}>
                Showing {(filteredAlumni ?? []).length} alumni
              </span>
            </div>

            {loadingAlumni ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: accentColor }} />
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {filteredAlumni.map((person, index) => (
                  <motion.div
                    key={person.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                    className={`${cardCls} relative overflow-hidden rounded-xl p-4 sm:p-6 transition-all ${(appliedConnectionFilters && person.is_match) ? 'blue-highlight' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold flex-1" style={{ fontFamily: "'Manrope', sans-serif", color: cardTitleText }}>
                        {person.name}
                      </h3>
                      <a
                        href={person.linkedin_url || buildLinkedInSearchUrl(person.name, getTopCompanyNamesForPerson(person))}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={person.linkedin_url ? `View ${person.name} on LinkedIn` : `Search ${person.name} on LinkedIn`}
                        className="inline-flex items-center transition-colors"
                        style={{ color: cardMutedText }}
                        onMouseEnter={e => e.currentTarget.style.color = '#93C5FD'}
                        onMouseLeave={e => e.currentTarget.style.color = cardMutedText}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                    <p className="text-sm mb-4" style={{ color: cardMutedText }}>
                      Exited {person.exited_year}
                    </p>
                    <div className="space-y-2">
                      {person.path && Array.isArray(person.path) && person.path.length > 0 ? (
                        person.path.map((company, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: cardMetaText }} />
                            <p className={`text-sm ${i === person.path.length - 1 ? 'font-medium' : ''}`}
                              style={{ color: i === person.path.length - 1 ? '#34D399' : cardBodyText }}>
                              {i === person.path.length - 1 && <span className="mr-1">📍</span>}
                              {company}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: cardMetaText }} />
                          <p className="text-sm font-medium" style={{ color: '#34D399' }}>
                            📍 {person.current_company}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
            {!loadingAlumni && alumniHasMore && (
              <div className="flex justify-center pt-6">
                <button
                  type="button"
                  onClick={loadMoreAlumni}
                  disabled={alumniLoadingMore}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                  style={isDark
                    ? { border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }
                    : { border: '1px solid #cbd5e1', color: '#334155', background: '#ffffff' }}
                >
                  {alumniLoadingMore ? 'Loading...' : 'Load more alumni'}
                </button>
              </div>
            )}
            {(filteredAlumni ?? []).length === 0 && !loadingAlumni && (
              <div className="text-center py-24">
                <p className="text-sm" style={{ color: txtMuted }}>{alumniNoMatchMessage}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'employees' && (
          <div className="space-y-6">
            <div className="dark-section-header-blue flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-6 px-4 sm:px-6 py-3.5 rounded-lg">
              <h2 className="text-base font-semibold" style={{ color: sectionBlueTitle }}>
                Employees ({employeesTotal || 0})
              </h2>
              <span className="text-sm" style={{ color: sectionBlueMeta }}>
                Showing {(filteredEmployees ?? []).length} of {employeesTotal || (filteredEmployees ?? []).length} current employees
              </span>
            </div>

            {loadingEmployees ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: accentColor }} />
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {filteredEmployees.map((person, index) => (
                  <motion.div
                    key={person.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                    className={`${cardCls} relative overflow-hidden rounded-xl p-4 sm:p-6 transition-all ${(appliedConnectionFilters && person.is_match) ? 'blue-highlight' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold flex-1" style={{ fontFamily: "'Manrope', sans-serif", color: cardTitleText }}>
                        {person.name}
                      </h3>
                      <a
                        href={person.linkedin_url || buildLinkedInSearchUrl(person.name, getTopCompanyNamesForPerson(person))}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={person.linkedin_url ? `View ${person.name} on LinkedIn` : `Search ${person.name} on LinkedIn`}
                        className="inline-flex items-center transition-colors"
                        style={{ color: cardMutedText }}
                        onMouseEnter={e => e.currentTarget.style.color = '#93C5FD'}
                        onMouseLeave={e => e.currentTarget.style.color = cardMutedText}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                    <p className="text-sm mb-2" style={{ color: cardMutedText }}>
                      Current role: {person.current_role || 'N/A'}
                    </p>
                    <p className="text-sm mb-4" style={{ color: '#34D399' }}>
                      📍 {person.current_company || topCompanyName}
                    </p>
                    <div className="space-y-2">
                      {person.path && Array.isArray(person.path) && person.path.length > 0 ? (
                        person.path.slice(-4).map((company, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: cardMetaText }} />
                            <p className="text-sm" style={{ color: cardBodyText }}>
                              {company}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm" style={{ color: txtMuted }}>
                          No previous company history available.
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
            {!loadingEmployees && employeesHasMore && (
              <div className="flex justify-center pt-6">
                <button
                  type="button"
                  onClick={loadMoreEmployees}
                  disabled={employeesLoadingMore}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                  style={isDark
                    ? { border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }
                    : { border: '1px solid #cbd5e1', color: '#334155', background: '#ffffff' }}
                >
                  {employeesLoadingMore ? 'Loading...' : 'Load more employees'}
                </button>
              </div>
            )}
            {(filteredEmployees ?? []).length === 0 && !loadingEmployees && (
              <div className="text-center py-24">
                <p className="text-sm" style={{ color: txtMuted }}>{employeesNoMatchMessage}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="text-center py-16">
            <p style={{ color: txtMuted }}>Statistics view coming soon...</p>
          </div>
        )}
      </div>
      {activeTab === 'company-pathways' && !loading && !error && (
        <div className="hidden xl:flex fixed right-5 top-1/2 -translate-y-1/2 z-30">
          <div className="bg-[#0d1019] border border-white/15 shadow-md rounded-xl p-2 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => scrollToTransitions(1)}
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-[#DBEAFE] text-[#1E40AF] hover:bg-[#BFDBFE] transition-colors"
            >
              1st
            </button>
            {(filteredCompaniesSecond ?? []).length > 0 && (
              <button
                type="button"
                onClick={() => scrollToTransitions(2)}
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-[#F3E8FF] text-[#6B21A8] hover:bg-[#E9D5FF] transition-colors"
              >
                2nd
              </button>
            )}
            {(filteredCompaniesThird ?? []).length > 0 && (
              <button
                type="button"
                onClick={() => scrollToTransitions(3)}
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0] transition-colors"
              >
                3+
              </button>
            )}
          </div>
        </div>
      )}

      <AnimatePresence>
        {selectedCount > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="fixed inset-x-0 bottom-3 sm:bottom-5 z-[100] px-3 sm:px-6 flex justify-center pointer-events-none"
          >
            <div
              className="max-w-[980px] w-full backdrop-blur-md rounded-2xl p-3 sm:p-3.5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 pointer-events-auto"
              style={bottomBarContainer}
            >
              <div
                className="w-9 h-9 rounded-xl font-bold text-sm flex items-center justify-center shrink-0"
                style={bottomBarCountPill}
              >
                {selectedCount}
              </div>
              <div className="min-w-0 sm:pr-4 w-full sm:w-auto">
                <p className="text-sm sm:text-base font-semibold truncate">{selectedSummaryText}</p>
                <p className="text-xs sm:text-sm inline-flex items-center gap-1.5" style={{ color: bottomBarSubtext }}>
                  <Sparkles className="w-3.5 h-3.5" />
                  Ready to find jobs
                </p>
              </div>
              <div className="w-full sm:w-auto sm:ml-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={clearSelectedCards}
                  className="h-10 px-4 sm:px-5 rounded-xl border transition-all font-semibold text-sm"
                  style={isDark
                    ? { borderColor: '#44403C', color: '#FAFAF9' }
                    : { borderColor: '#cbd5e1', color: '#334155', background: '#ffffff' }}
                >
                  Clear Selection
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackFindingJobs({
                      source: 'dashboard',
                      action: 'open_modal',
                      selected_company_count: selectedCompanyNames.length,
                      selected_company_names: selectedCompanyNames.slice(0, 20),
                    });
                    setIsRoleModalOpen(true);
                  }}
                  className="h-10 px-4 sm:px-6 rounded-xl font-bold transition-all inline-flex items-center justify-center text-sm"
                  style={isDark
                    ? { background: accentColor, color: '#1A1612', boxShadow: '0 8px 20px rgba(245,158,11,0.35)' }
                    : { background: '#2563eb', color: '#ffffff', boxShadow: '0 8px 20px rgba(37,99,235,0.25)' }}
                >
                  <span className="relative mr-2 inline-flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-80" style={{ background: isDark ? 'rgba(26,22,18,0.6)' : 'rgba(255,255,255,0.55)' }} />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: isDark ? '#1A1612' : '#ffffff' }} />
                  </span>
                  Find Jobs
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Role Selection Modal */}
      <AnimatePresence>
        {isRoleModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-end justify-center p-0 bg-black/55 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="bg-[#0d1019]/95 rounded-t-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border-t border-white/10"
            >
              <div className="flex flex-col items-center pt-4 pb-2">
                <div className="w-12 h-1.5 bg-white/25 rounded-full" />
              </div>
              <div className="p-4 sm:p-8 pt-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[${accentColor}]/15 flex items-center justify-center">
                    <Search className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white/90" style={{ fontFamily: "'Playfair Display', serif" }}>
                    What role are you looking for?
                  </h2>
                </div>
                <p className="text-sm text-white/50 mb-8 line-clamp-2">
                  Searching at <span className="font-semibold text-white/85">{selectedCompanyNames.join(', ')}</span>
                </p>

                <div className="relative mb-6">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/35" />
                  <input
                    autoFocus
                    type="text"
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && roleInput.trim()) {
                        if (!searchRoles.includes(roleInput.trim())) {
                          updateSearchRoles([...searchRoles, roleInput.trim()]);
                        }
                        setRoleInput('');
                      }
                    }}
                    placeholder="Search or type a role..."
                    className="w-full pl-12 pr-4 h-14 !bg-white/5 border border-white/15 rounded-2xl !text-white/90 placeholder:text-white/35 focus:outline-none focus:border-[#F59E0B]/60 focus:ring-4 focus:ring-[#F59E0B]/10 transition-all"
                  />
                  {roleInput.trim() && (
                    <button
                      onClick={() => {
                        if (!searchRoles.includes(roleInput.trim())) {
                          updateSearchRoles([...searchRoles, roleInput.trim()]);
                        }
                        setRoleInput('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-8 px-3 bg-[${accentColor}] text-[#1A1612] text-xs font-bold rounded-lg hover:bg-[#D97706] transition-all"
                    >
                      Add
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-8 min-h-[40px]">
                  {searchRoles.map((role) => (
                    <button
                      key={role}
                      onClick={() => updateSearchRoles(searchRoles.filter(r => r !== role))}
                      className="group flex items-center gap-2 px-4 py-2 bg-[#1F2937] hover:bg-[#2B3545] text-[#E2E8F0] hover:text-[#FCA5A5] rounded-full text-sm font-medium border border-[#334155] hover:border-[#475569] transition-all"                    >
                      {role}
                      <X className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                    </button>
                  ))}
                  {searchRoles.length === 0 && (
                    <p className="text-sm text-white/35 italic mt-2">No roles added yet. Press Enter to add tags.</p>
                  )}
                </div>

                <div className="flex flex-col gap-3 pb-8">
                  <button
                    disabled={searchRoles.length === 0}
                    onClick={() => openLinkedInJobsForSelection(searchRoles)}
                    className={`h-14 w-full rounded-2xl font-bold flex items-center justify-center transition-all ${searchRoles.length > 0
                      ? 'bg-[${accentColor}] text-[#1A1612] hover:bg-[#D97706] shadow-lg shadow-[#F59E0B]/20'
                      : 'bg-white/10 text-white/35 cursor-not-allowed'
                      }`}
                  >
                    {searchRoles.length > 0 ? `Find at ${selectedCount} Companies` : 'Select at least one role'}
                  </button>
                  <button
                    onClick={() => {
                      setIsRoleModalOpen(false);
                      setRoleInput('');
                    }}
                    className="h-12 w-full text-white/55 font-semibold hover:text-white/90 transition-all text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;



// Triggering redeploy:   no functional changes
